import {
  HEADER_SECONDARY_BUTTON,
  HEADER_SECONDARY_ICON,
} from "../components/layout/headerActionStyles";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BarChart3,
  Bot,
  Camera,
  Check,
  CheckCircle,
  Clock,
  Edit3,
  Frown,
  Globe,
  HelpCircle,
  Kanban,
  List,
  ListFilter,
  Mail,
  Meh,
  MessageCircle,
  MessageSquare,
  MoreHorizontal,
  MoreVertical,
  Plus,
  RefreshCw,
  Search,
  Send,
  Settings,
  ShieldAlert,
  Smile,
  Tag,
  ThumbsUp,
  Trash2,
  TrendingUp,
  UserPlus,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ActionDropdown,
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  PageLayout,
  ResponsiveGrid,
  Select,
  Textarea,
  Tooltip as UITooltip,
} from "../components";
import { AgentConfigPanel } from "../components/auto_responder/AgentConfigPanel";
import AutoResponderKanban from "../components/auto_responder/AutoResponderKanban";
import { AutoResponderSkeleton } from "../components/skeletons";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { showToastError, showToastSuccess } from "../shared/alertService";
import apiService, { API_ENDPOINTS } from "../shared/apiService";
import { cn } from "../utils/cn";
import {
  PlatformIcon,
  getPlatformChipClasses,
  getPlatformConfig,
  getPlatformName,
} from "../utils/platformIcons";

interface AutoResponse {
  id: string;
  trigger: string;
  response: string;
  confidence: number;
  status: "pending" | "approved" | "rejected" | "sent" | "responded";
  platform: string;
  user_id: string;
  post_id?: string;
  post_urn?: string;
  reply_id?: string;
  created_at: string;
  approved_at?: string;
  sent_at?: string;
  error?: string;
  retry_count?: number;
  sentiment?: "positive" | "negative" | "neutral";
  sentiment_score?: number;
  message_type?: string;
  source_detail?: string;
  source_channel?: string;
  custom_tags?: number[];
  origin_post_id?: number | null;
  organization_id?: number | null;
  client_company_id?: number | null;
  lead_id?: string | null;
  metadata?: {
    post_id?: string;
    internal_post_id?: number | null;
    conversation_history?: any[];
    [key: string]: any;
  };
}

const TAG_NAME_MAX_LENGTH = 30;

const MESSAGE_TYPE_PT: Record<string, string> = {
  compliment: "Elogio",
  complaint: "Reclamação",
  question: "Pergunta",
  lead: "Lead",
  spam: "Spam",
  other: "Outro",
};

interface CustomTag {
  id: number;
  name: string;
  color: string;
}

interface SentimentStats {
  sentiment: Record<string, number>;
  message_types: Record<string, number>;
  channels: Record<string, number>;
  platforms: Record<string, number>;
}

interface ResponseTemplate {
  id: string;
  name: string;
  content: string;
  trigger_keywords: string[];
  platform: string;
  is_active: boolean;
  usage_count: number;
}

// Sugestões de templates prontas para uso — apenas ponto de partida editável,
// nunca inseridas como se já fossem registros reais salvos.
const TEMPLATE_SUGGESTIONS: {
  name: string;
  content: string;
  trigger_keywords: string;
}[] = [
  {
    name: "Saudação Padrão",
    content:
      "Olá! Obrigado por entrar em contato. Como posso ajudar você hoje?",
    trigger_keywords: "olá, oi, bom dia, boa tarde",
  },
  {
    name: "Suporte Técnico",
    content:
      "Entendo sua dúvida técnica. Vou conectar você com nossa equipe de suporte especializada.",
    trigger_keywords: "problema, erro, bug, não funciona",
  },
  {
    name: "Informações de Preço",
    content:
      "Posso enviar nossa tabela de preços atualizada. Qual seu email para envio?",
    trigger_keywords: "preço, valor, custo, quanto custa",
  },
];

interface AutoResponderStats {
  total_responses: number;
  pending_approvals: number;
  approved_responses: number;
  rejected_responses: number;
  auto_approval_rate: number;
  response_time_avg: number;
  satisfaction_score: number;
}

function formatRelativeTime(date: Date, t: (key: string) => string): string {
  const seconds = Math.max(0, (Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return t("agora mesmo");
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${t("há")} ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${t("há")} ${hours}h`;
  const days = Math.floor(hours / 24);
  return `${t("há")} ${days}d`;
}

function formatResponseTime(seconds: number): string {
  if (!seconds || seconds <= 0) return "0s";

  const MINUTE = 60;
  const HOUR = 3600;
  const DAY = 86400;
  const MONTH = DAY * 30;

  const units: [number, string][] = [
    [MONTH, "m"],
    [DAY, "d"],
    [HOUR, "h"],
    [MINUTE, "min"],
    [1, "s"],
  ];

  const unitIndex = units.findIndex(([size]) => seconds >= size);
  if (unitIndex === -1) return `${Math.round(seconds)}s`;

  const [size, label] = units[unitIndex];
  const whole = Math.floor(seconds / size);
  const remainder = seconds - whole * size;

  // Segundos não têm subunidade menor para compor.
  if (unitIndex === units.length - 1) return `${whole}s`;

  const [nextSize, nextLabel] = units[unitIndex + 1];
  const nextWhole = Math.floor(remainder / nextSize);

  return nextWhole > 0
    ? `${whole}${label} ${nextWhole}${nextLabel}`
    : `${whole}${label}`;
}

interface PlatformConfig {
  enabled: boolean;
  using_rag: boolean;
  auto_approve: boolean;
  min_confidence: number;
  max_responses_per_day: number;
  last_active?: string;
  prompt?: string;
  knowledge_urls?: string[];
  handoff_triggers?: string[];
  handoff_message?: string;
  model_id?: string;
}

interface PlatformStatus {
  global?: PlatformConfig;
  instagram: PlatformConfig;
  facebook: PlatformConfig;
  linkedin: PlatformConfig;
  google_my_business: PlatformConfig;
  messenger?: PlatformConfig;
}

const getStatusConfig = (status: string) => {
  switch (status) {
    case "approved":
      return {
        color: "emerald",
        variant: "success",
        label: "Aprovado",
        icon: CheckCircle,
      };
    case "pending":
      return {
        color: "amber",
        variant: "warning",
        label: "Pendente",
        icon: Clock,
      };
    case "rejected":
      return {
        color: "red",
        variant: "destructive",
        label: "Rejeitado",
        icon: XCircle,
      };
    case "sent":
      return {
        color: "blue",
        variant: "info",
        label: "Enviado",
        icon: MessageSquare,
      };
    case "responded":
      return {
        color: "purple",
        variant: "purple",
        label: "Respondido",
        icon: Bot,
      };
    default:
      return {
        color: "gray",
        variant: "secondary",
        label: status,
        icon: Activity,
      };
  }
};

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}> = ({ icon, title, subtitle, action }) => (
  <div className="flex items-start gap-3 border-b pb-2 border-purple-100 mb-3">
    <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-purple-600 rounded-xl text-white shadow-xs">
      {icon}
    </div>
    <div className="flex-1 min-w-0 h-10 flex flex-col justify-between">
      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight leading-none">
        {title}
      </h3>
      <span className="text-xs text-gray-400 font-medium leading-none">
        {subtitle}
      </span>
    </div>
    {action}
  </div>
);

const AutoResponder: React.FC = () => {
  const { t } = useTranslation();
  const { activeContext, isLoading: contextLoading } = useActiveContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = [
    "monitoramento",
    "historico",
    "conhecimento",
    "configuracao",
  ];
  const [activeTab, setActiveTab] = useState<
    "monitoramento" | "historico" | "conhecimento" | "configuracao"
  >(() => {
    const fromUrl = searchParams.get("tab");
    return (
      fromUrl && VALID_TABS.includes(fromUrl) ? fromUrl : "monitoramento"
    ) as "monitoramento" | "historico" | "conhecimento" | "configuracao";
  });

  // Mantém a URL sincronizada com a aba ativa — permite recarregar a página
  // ou compartilhar um link direto para uma aba específica.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", activeTab);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const [responses, setResponses] = useState<AutoResponse[]>([]);
  const [templates, setTemplates] = useState<ResponseTemplate[]>([]);
  const [stats, setStats] = useState<AutoResponderStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [selectedResponse, setSelectedResponse] = useState<AutoResponse | null>(
    null,
  );
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<ResponseTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    content: "",
    trigger_keywords: "",
    platform: "all",
    is_active: true,
  });
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateDeletingId, setTemplateDeletingId] = useState<string | null>(
    null,
  );
  const [knowledgeUrlsText, setKnowledgeUrlsText] = useState("");
  const [knowledgeUrlsSaving, setKnowledgeUrlsSaving] = useState(false);
  const knowledgeUrlsInitialized = useRef(false);
  const [filters, setFilters] = useState({
    status: "all",
    platform: "all",
    date_range: "7d",
    query: "",
    sentiment: "all",
    message_type: "all",
    source_channel: "all",
    tag: "all",
  });
  const [editingResponseText, setEditingResponseText] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [modalActionLoading, setModalActionLoading] = useState<
    "update" | "approve" | "send" | "classification" | null
  >(null);
  const [responsesView, setResponsesView] = useState<"list" | "kanban">(
    "kanban",
  );
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [platformStatus, setPlatformStatus] = useState<PlatformStatus | null>(
    null,
  );
  const [customTags, setCustomTags] = useState<CustomTag[]>([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [sentimentStats, setSentimentStats] = useState<SentimentStats | null>(
    null,
  );
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#8b5cf6");
  const [tagLoading, setTagLoading] = useState(false);
  const [tagDeletingId, setTagDeletingId] = useState<number | null>(null);
  // Editing fields for detail modal
  const [editingSentiment, setEditingSentiment] = useState<string>("");
  const [editingMessageType, setEditingMessageType] = useState<string>("");
  const [editingChannel, setEditingChannel] = useState<string>("");
  const [editingCustomTags, setEditingCustomTags] = useState<number[]>([]);
  const [testingReply, setTestingReply] = useState({
    platform: "instagram",
    id: "",
    postIdOrUrn: "",
    text: "",
  });
  const [testResult, setTestResult] = useState<{
    response: string;
    sentiment: string;
    message_type: string;
  } | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [originPost, setOriginPost] = useState<{
    permalink?: string;
    caption?: string;
    media_url?: string;
    media_type?: string;
    timestamp?: string;
    platform?: string;
  } | null>(null);
  const [originPostLoading, setOriginPostLoading] = useState(false);
  const [showOriginModal, setShowOriginModal] = useState(false);

  // Deduplica chamadas concorrentes/consecutivas ao mesmo round de fetch —
  // cobre tanto o double-invoke de efeitos do StrictMode em dev quanto
  // qualquer re-disparo redundante do efeito de carregamento inicial.
  const loadInFlightRef = useRef(false);

  // Templates (Base de Conhecimento) tem seu próprio ciclo de carregamento/erro,
  // independente do restante (Monitoramento/Histórico) — uma falha num não deve
  // bloquear nem mascarar o estado do outro.
  const loadTemplates = useCallback(async () => {
    setTemplatesError(null);
    try {
      const templatesData = await apiService.get(
        API_ENDPOINTS.AUTO_RESPONDER.TEMPLATES,
      );
      setTemplates(templatesData.templates || []);
    } catch (err) {
      console.error("Erro ao carregar templates:", err);
      setTemplatesError(
        err instanceof Error ? err.message : t("Erro ao carregar templates"),
      );
    }
  }, [t]);

  const loadAutoResponderData = useCallback(async () => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    setLoading(true);
    setError(null);

    loadTemplates();

    try {
      // Carregar dados em paralelo usando apiService
      // NOTA: monitor_comments NÃO é chamado aqui — ele roda via worker em background
      // (a cada INTERVAL segundos). Chamar em todo load esgotaria rate limits das APIs sociais.
      const [responsesData, statsData, statusData, tagsData, sentimentData] =
        await Promise.all([
          apiService.get(`${API_ENDPOINTS.AUTO_RESPONDER.RESPONSES}?limit=500`),
          apiService.get(API_ENDPOINTS.AUTO_RESPONDER.STATS),
          apiService.get(API_ENDPOINTS.AUTO_RESPONDER.STATUS),
          apiService
            .get(API_ENDPOINTS.AUTO_RESPONDER.CUSTOM_TAGS)
            .catch(() => ({ custom_tags: [] })),
          apiService
            .get(API_ENDPOINTS.AUTO_RESPONDER.SENTIMENT_STATS)
            .catch(() => null),
        ]);

      setResponses(responsesData.responses || []);
      setStats(statsData.stats || statsData);
      setPlatformStatus(statusData.status || null);
      setCustomTags(tagsData.custom_tags || []);
      if (sentimentData?.success) setSentimentStats(sentimentData);
      setLastRefreshedAt(new Date());
    } catch (err) {
      console.error("Erro ao carregar dados do auto-responder:", err);
      setError(
        err instanceof Error ? err.message : t("Erro ao carregar dados"),
      );
    } finally {
      setLoading(false);
      loadInFlightRef.current = false;
    }
  }, [
    activeContext?.type,
    activeContext?.organization?.id,
    activeContext?.clientCompany?.id,
  ]);

  const loadPlatformStatus = useCallback(async () => {
    try {
      const data = await apiService.get(API_ENDPOINTS.AUTO_RESPONDER.STATUS);
      setPlatformStatus(data.status);
    } catch (e) {
      console.error("Erro ao buscar status do auto-responder", e);
    }
  }, [
    activeContext?.type,
    activeContext?.organization?.id,
    activeContext?.clientCompany?.id,
  ]);

  // Sincroniza o textarea de URLs de conhecimento (RAG) assim que o status
  // global carrega — só na primeira vez, para não sobrescrever edição em curso.
  useEffect(() => {
    if (knowledgeUrlsInitialized.current) return;
    if (!platformStatus?.global) return;
    setKnowledgeUrlsText(
      (platformStatus.global.knowledge_urls || []).join("\n"),
    );
    knowledgeUrlsInitialized.current = true;
  }, [platformStatus?.global]);

  const handleSaveKnowledgeUrls = async () => {
    setKnowledgeUrlsSaving(true);
    try {
      const urls = knowledgeUrlsText
        .split("\n")
        .map((u) => u.trim())
        .filter(Boolean);
      await updateGlobalAgent({ knowledge_urls: urls });
    } finally {
      setKnowledgeUrlsSaving(false);
    }
  };

  const updatePlatform = async (
    platform:
      | "instagram"
      | "facebook"
      | "linkedin"
      | "google_my_business"
      | "messenger",
    updates: {
      enabled?: boolean;
      auto_approve?: boolean;
      min_confidence?: number;
      prompt?: string;
      knowledge_urls?: string[];
      handoff_triggers?: string[];
      handoff_message?: string;
      model_id?: string;
    },
  ) => {
    try {
      const currentStatus = platformStatus?.[platform];
      const result = await apiService.post(
        API_ENDPOINTS.AUTO_RESPONDER.ENABLE,
        {
          platform,
          enabled: updates.enabled ?? currentStatus?.enabled ?? false,
          auto_approve:
            updates.auto_approve ?? currentStatus?.auto_approve ?? false,
          min_confidence:
            updates.min_confidence ?? currentStatus?.min_confidence ?? 0.8,
          max_responses_per_day: currentStatus?.max_responses_per_day ?? 50,
          prompt:
            updates.prompt !== undefined
              ? updates.prompt
              : currentStatus?.prompt,
          knowledge_urls:
            updates.knowledge_urls !== undefined
              ? updates.knowledge_urls
              : currentStatus?.knowledge_urls,
          handoff_triggers:
            updates.handoff_triggers !== undefined
              ? updates.handoff_triggers
              : currentStatus?.handoff_triggers,
          handoff_message:
            updates.handoff_message !== undefined
              ? updates.handoff_message
              : currentStatus?.handoff_message,
          model_id:
            updates.model_id !== undefined
              ? updates.model_id
              : currentStatus?.model_id,
        },
      );
      // Slider de confiança dispara onChange a cada pixel arrastado — evita
      // spam de toast nesse caso específico, mantendo feedback nos toggles.
      const isOnlyConfidenceDrag =
        updates.min_confidence !== undefined &&
        updates.enabled === undefined &&
        updates.auto_approve === undefined;
      if (!isOnlyConfidenceDrag) {
        showToastSuccess(result.message || t("Configuração atualizada"));
      }
      await loadPlatformStatus();
    } catch (e: any) {
      showToastError(e.message || t("Erro ao atualizar configuração"));
    }
  };

  const updateGlobalAgent = async (updates: any) => {
    try {
      await apiService.post("/api/auto-responder/global-agent", updates);
      await loadPlatformStatus();
      showToastSuccess(t("Configurações do Agente IA atualizadas!"));
    } catch (e) {
      console.error("Erro ao atualizar Agente IA global", e);
      showToastError(t("Erro ao salvar configurações do Agente IA"));
    }
  };

  const replyWithAI = async () => {
    const { platform, id, postIdOrUrn, text } = testingReply;
    if (!text.trim()) {
      showToastError(t("Digite o texto do cliente para simular"));
      return;
    }
    setTestLoading(true);
    setTestResult(null);
    try {
      const body: Record<string, string> = {
        platform,
        comment_id: id || `sim_${Date.now()}`,
        comment_text: text,
        username: "João (Teste)",
      };
      if (platform === "linkedin" && postIdOrUrn) body.post_urn = postIdOrUrn;

      const data = await apiService.post(
        API_ENDPOINTS.AUTO_RESPONDER.PROCESS_COMMENT,
        body,
      );
      const r = data?.response;
      if (r) {
        setTestResult({
          response: r.response ?? "",
          sentiment: r.sentiment ?? "neutral",
          message_type: r.message_type ?? "other",
        });
      } else {
        showToastError(data?.error || t("Sem resposta gerada"));
      }
    } catch (e: any) {
      showToastError(e.message || t("Erro ao gerar resposta IA"));
    } finally {
      setTestLoading(false);
    }
  };

  const fetchOriginPost = async (responseId: string) => {
    setOriginPost(null);
    setOriginPostLoading(true);
    setShowOriginModal(true);
    try {
      const url = API_ENDPOINTS.AUTO_RESPONDER.ORIGIN_POST.replace(
        "{id}",
        responseId,
      );
      const data = await apiService.get(url);
      if (data?.success && data.post) setOriginPost(data.post);
    } catch {
      setOriginPost(null);
    } finally {
      setOriginPostLoading(false);
    }
  };

  const openResponseModal = (r: AutoResponse) => {
    setSelectedResponse(r);
    setEditingResponseText(r.response);
    setEditingSentiment(r.sentiment ?? "");
    setEditingMessageType(r.message_type ?? "");
    setEditingChannel(r.source_channel ?? "");
    setEditingCustomTags(r.custom_tags ?? []);
    setSuggestions([]);
    setModalActionLoading(null);
    setShowResponseModal(true);
  };

  const handleFetchSuggestions = async () => {
    if (!selectedResponse) return;
    setLoadingSuggestions(true);
    setSuggestions([]);
    try {
      const url = API_ENDPOINTS.AUTO_RESPONDER.SUGGESTIONS.replace(
        "{id}",
        selectedResponse.id,
      );
      const data = await apiService.get(url);
      if (data.suggestions && data.suggestions.length > 0) {
        setSuggestions(data.suggestions);
      } else {
        showToastError(t("Nenhuma alternativa foi gerada. Tente novamente."));
      }
    } catch (e: any) {
      showToastError(e.message || t("Erro ao gerar alternativas"));
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSaveClassification = async (responseId: string) => {
    try {
      await apiService.put(
        `${API_ENDPOINTS.AUTO_RESPONDER.RESPONSES}/${responseId}`,
        {
          sentiment: editingSentiment || undefined,
          message_type: editingMessageType || undefined,
          source_channel: editingChannel || undefined,
          custom_tags: editingCustomTags,
        },
      );
      loadAutoResponderData();
      showToastSuccess(t("Classificação salva"));
    } catch (err) {
      showToastError(t("Erro ao salvar classificação"));
    }
  };

  const openTemplateModal = (template: ResponseTemplate | null) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template?.name ?? "",
      content: template?.content ?? "",
      trigger_keywords: template?.trigger_keywords?.join(", ") ?? "",
      platform: template?.platform ?? "all",
      is_active: template?.is_active ?? true,
    });
    setShowTemplateModal(true);
  };

  // Pré-preenche o formulário com uma sugestão — sempre cria um template novo
  // (nunca edita), já que a sugestão não é um registro real salvo.
  const openTemplateSuggestion = (
    suggestion: (typeof TEMPLATE_SUGGESTIONS)[number],
  ) => {
    setEditingTemplate(null);
    setTemplateForm({
      name: suggestion.name,
      content: suggestion.content,
      trigger_keywords: suggestion.trigger_keywords,
      platform: "all",
      is_active: true,
    });
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.content.trim()) {
      showToastError(t("Preencha nome e conteúdo do template"));
      return;
    }
    setTemplateSaving(true);
    try {
      const payload = {
        name: templateForm.name.trim(),
        content: templateForm.content.trim(),
        trigger_keywords: templateForm.trigger_keywords
          .split(",")
          .map((k) => k.trim())
          .filter(Boolean),
        platform: templateForm.platform,
        is_active: templateForm.is_active,
      };
      if (editingTemplate) {
        await apiService.put(
          `${API_ENDPOINTS.AUTO_RESPONDER.TEMPLATES}/${editingTemplate.id}`,
          payload,
        );
        showToastSuccess(t("Template atualizado com sucesso"));
      } else {
        await apiService.post(API_ENDPOINTS.AUTO_RESPONDER.TEMPLATES, payload);
        showToastSuccess(t("Template criado com sucesso"));
      }
      setShowTemplateModal(false);
      loadTemplates();
    } catch (err: any) {
      showToastError(err?.message || t("Erro ao salvar template"));
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm(t("Tem certeza que deseja excluir este template?"))) {
      return;
    }
    setTemplateDeletingId(templateId);
    try {
      await apiService.delete(
        `${API_ENDPOINTS.AUTO_RESPONDER.TEMPLATES}/${templateId}`,
      );
      showToastSuccess(t("Template excluído"));
      loadTemplates();
    } catch (err: any) {
      showToastError(err?.message || t("Erro ao excluir template"));
    } finally {
      setTemplateDeletingId(null);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setTagLoading(true);
    try {
      const data = await apiService.post(
        API_ENDPOINTS.AUTO_RESPONDER.CUSTOM_TAGS,
        {
          name: newTagName.trim(),
          color: newTagColor,
        },
      );
      setCustomTags((prev) => [...prev, data.tag]);
      setNewTagName("");
      setNewTagColor("#8b5cf6");
      showToastSuccess(t("Tag criada"));
    } catch (err) {
      showToastError(t("Erro ao criar tag"));
    } finally {
      setTagLoading(false);
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    if (tagDeletingId !== null) return;
    setTagDeletingId(tagId);
    try {
      await apiService.delete(
        API_ENDPOINTS.AUTO_RESPONDER.CUSTOM_TAG_DELETE.replace(
          "{id}",
          String(tagId),
        ),
      );
      setCustomTags((prev) => prev.filter((t) => t.id !== tagId));
      showToastSuccess(t("Tag removida"));
    } catch (err: any) {
      showToastError(err?.message || t("Erro ao remover tag"));
    } finally {
      setTagDeletingId(null);
    }
  };

  const handleApproveResponse = async (responseId: string) => {
    try {
      await apiService.post(
        API_ENDPOINTS.AUTO_RESPONDER.APPROVE.replace("{id}", responseId),
        {
          user_id: "current_user",
        },
      );

      loadAutoResponderData();
      showToastSuccess(t("Resposta aprovada com sucesso"));
    } catch (err) {
      console.error("Erro ao aprovar resposta:", err);
      showToastError(t("Erro ao aprovar resposta"));
    }
  };

  const handleSendResponse = async (
    responseId: string,
    responseObj?: AutoResponse,
  ) => {
    // Aviso prévio para Instagram Direct — requer permissão especial no app Meta
    if (responseObj?.source_channel === "direct") {
      const confirmed = window.confirm(
        t(
          "⚠️ Instagram Direct requer a permissão 'instagram_manage_messages' no app Meta.\n\n" +
            "Se esta permissão não estiver ativa, o envio falhará com erro (#3).\n\n" +
            "Para ativar:\n" +
            "1. Acesse o Meta Developer Portal → App 'Virtus Bot'\n" +
            "2. App Review → Permissions → adicione 'instagram_manage_messages'\n" +
            "3. Reconecte via Autenticação Automática nas configurações\n\n" +
            "Deseja tentar enviar mesmo assim?",
        ),
      );
      if (!confirmed) return;
    }
    try {
      await apiService.post(`/api/auto-responder/send/${responseId}`, {});

      loadAutoResponderData();
      showToastSuccess(t("Resposta enviada com sucesso"));
    } catch (err: any) {
      console.error("Erro ao enviar resposta:", err);
      const userMsg =
        err?.message || err?.error || t("Erro ao enviar resposta");
      showToastError(userMsg);
    }
  };

  const handleUpdateResponse = async (responseId: string) => {
    try {
      await apiService.put(
        `${API_ENDPOINTS.AUTO_RESPONDER.RESPONSES}/${responseId}`,
        {
          response_text: editingResponseText,
        },
      );

      loadAutoResponderData();
      showToastSuccess(t("Resposta atualizada com sucesso"));
    } catch (err) {
      console.error("Erro ao atualizar resposta:", err);
      showToastError(t("Erro ao atualizar resposta"));
    }
  };

  const handleRejectResponse = async (responseId: string, reason: string) => {
    try {
      await apiService.post(
        API_ENDPOINTS.AUTO_RESPONDER.REJECT.replace("{id}", responseId),
        {
          user_id: "current_user",
          reason: reason,
        },
      );

      loadAutoResponderData();
      showToastSuccess(t("Resposta rejeitada"));
    } catch (err) {
      console.error("Erro ao rejeitar resposta:", err);
      showToastError(t("Erro ao rejeitar resposta"));
    }
  };

  // ── Seleção múltipla e ações em lote ─────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Ação em andamento no dropdown de um card específico (evita disparos concorrentes na mesma linha)
  const [rowActionLoading, setRowActionLoading] = useState<string | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [platformActionLoading, setPlatformActionLoading] = useState<
    string | null
  >(null);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = (ids: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(ids);
    });
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    const total = selectedIds.size;
    let ok = 0;
    for (const id of selectedIds) {
      try {
        await apiService.post(
          API_ENDPOINTS.AUTO_RESPONDER.APPROVE.replace("{id}", id),
          {},
        );
        ok++;
      } catch {
        // continua os demais — falhas são reportadas no total ao final
      }
    }
    setSelectedIds(new Set());
    setBulkLoading(false);
    if (ok === total) {
      showToastSuccess(t("{ok} resposta(s) aprovada(s)", { ok: String(ok) }));
    } else if (ok > 0) {
      showToastError(
        t("{ok} de {total} respostas aprovadas — algumas falharam", {
          ok: String(ok),
          total: String(total),
        }),
      );
    } else {
      showToastError(t("Não foi possível aprovar as respostas selecionadas"));
    }
    loadAutoResponderData();
  };

  const handleBulkReject = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    const total = selectedIds.size;
    let ok = 0;
    for (const id of selectedIds) {
      try {
        await apiService.post(
          API_ENDPOINTS.AUTO_RESPONDER.REJECT.replace("{id}", id),
          { reason: "Rejeitado em lote" },
        );
        ok++;
      } catch {
        // continua os demais — falhas são reportadas no total ao final
      }
    }
    setSelectedIds(new Set());
    setBulkLoading(false);
    if (ok === total) {
      showToastSuccess(t("{ok} resposta(s) rejeitada(s)", { ok: String(ok) }));
    } else if (ok > 0) {
      showToastError(
        t("{ok} de {total} respostas rejeitadas — algumas falharam", {
          ok: String(ok),
          total: String(total),
        }),
      );
    } else {
      showToastError(t("Não foi possível rejeitar as respostas selecionadas"));
    }
    loadAutoResponderData();
  };

  const handleManualMonitor = async () => {
    if (monitorLoading) return;
    setMonitorLoading(true);
    try {
      await apiService.post(API_ENDPOINTS.AUTO_RESPONDER.MONITOR_COMMENTS, {});
      showToastSuccess(
        t("Monitoramento disparado. Novas mensagens aparecerão em instantes."),
      );
      setTimeout(() => loadAutoResponderData(), 3000);
    } catch (err: any) {
      showToastError(err?.message || t("Erro ao disparar monitoramento"));
    } finally {
      setMonitorLoading(false);
    }
  };

  const isAgentOperational = !!platformStatus?.global?.enabled;

  const tabs = [
    { id: "monitoramento", label: t("Monitoramento"), icon: TrendingUp },
    { id: "historico", label: t("Histórico"), icon: MessageSquare },
    { id: "conhecimento", label: t("Base de Conhecimento"), icon: Bot },
    { id: "configuracao", label: t("Configuração"), icon: Settings },
  ];

  // Track context to reset state synchronously and avoid flicker
  const contextId = `${activeContext.type}-${activeContext.organization?.id || ""}-${activeContext.clientCompany?.id || ""}`;
  const [lastContextId, setLastContextId] = useState(contextId);

  if (contextId !== lastContextId) {
    setResponses([]);
    setTemplates([]);
    setStats(null);
    setPlatformStatus(null);
    setLoading(true);
    setLastContextId(contextId);
  }

  // Carregar dados quando o componente monta — aguarda o contexto ativo
  // (organização/unidade) resolver primeiro, senão dispara um round de
  // requisições sem org_id que é imediatamente descartado quando o
  // contexto real chega logo em seguida (dobra as chamadas à toa).
  useEffect(() => {
    if (contextLoading) return;

    // Verificar se há um "code" de OAuth (Meta Login) na URL
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");

    if (code) {
      showToastSuccess(t("Conectando ao Instagram... Aguarde."));
      const currentUrl = window.location.origin + window.location.pathname; // URL sem parâmetros

      apiService
        .post("/api/auth/meta/exchange", {
          code,
          redirect_uri: currentUrl,
        })
        .then((res: any) => {
          if (res.success) {
            showToastSuccess(t("Instagram conectado com sucesso!"));
            // Remover os query params da URL
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname,
            );
            loadAutoResponderData();
          } else {
            showToastError(
              t("Erro ao conectar Instagram: ") +
                (res.error || t("Desconhecido")),
            );
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname,
            );
            loadAutoResponderData();
          }
        })
        .catch((err) => {
          showToastError(
            t("Erro na integração Meta: ") +
              (err?.response?.data?.error || err.message),
          );
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
          loadAutoResponderData();
        });
    } else {
      loadAutoResponderData();
    }
  }, [loadAutoResponderData, contextLoading]);

  // Interações "vazias" (sem texto/reação) que nunca devem aparecer nem contar
  const isTextlessResponse = (response: AutoResponse) => {
    const trig = (response.trigger || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "");
    return (
      trig.includes("[interacao sem texto") ||
      trig.includes("[interacao/reacao") ||
      trig.includes("interação sem texto")
    );
  };

  const responseMatchesFilters = (
    response: AutoResponse,
    f: typeof filters,
  ) => {
    if (f.status !== "all" && response.status !== f.status) return false;

    if (f.platform !== "all") {
      if (f.platform === "instagram") {
        const isInstagram =
          response.platform === "instagram" ||
          (response.platform === "messenger" &&
            (response.source_channel === "direct" ||
              response.metadata?.platform_type === "instagram" ||
              response.source_channel === "story_reply"));
        if (!isInstagram) return false;
      } else if (f.platform === "facebook") {
        const isFacebook =
          response.platform === "facebook" ||
          (response.platform === "messenger" &&
            response.source_channel === "messenger" &&
            response.metadata?.platform_type !== "instagram");
        if (!isFacebook) return false;
      } else if (f.platform === "messenger") {
        const isRealMessenger =
          response.platform === "messenger" &&
          response.source_channel !== "direct" &&
          response.source_channel !== "story_reply" &&
          response.metadata?.platform_type !== "instagram";
        if (!isRealMessenger) return false;
      } else {
        if (response.platform !== f.platform) return false;
      }
    }

    if (f.sentiment !== "all" && response.sentiment !== f.sentiment)
      return false;
    if (f.message_type !== "all" && response.message_type !== f.message_type)
      return false;
    if (
      f.source_channel !== "all" &&
      response.source_channel !== f.source_channel
    )
      return false;
    if (
      f.query &&
      !response.trigger.toLowerCase().includes(f.query.toLowerCase()) &&
      !response.response.toLowerCase().includes(f.query.toLowerCase())
    )
      return false;
    if (
      f.tag !== "all" &&
      !(response.custom_tags || []).includes(Number(f.tag))
    )
      return false;
    return true;
  };

  // Filtrar respostas baseado nos filtros
  const filteredResponses = useMemo(() => {
    if (!responses || responses.length === 0) return [];

    const result = responses.filter(
      (response) =>
        !isTextlessResponse(response) &&
        responseMatchesFilters(response, filters),
    );

    // Sort
    return result.sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, [responses, filters, sortOrder]);

  // Contadores por opção de filtro (cross-filtrado pelas demais dimensões
  // ativas, mesmo padrão de countGroups usado na página Postagens).
  const filterCounts = useMemo(() => {
    const base = (responses || []).filter((r) => !isTextlessResponse(r));
    const countFor = (dimension: keyof typeof filters, value: string) =>
      base.filter((r) =>
        responseMatchesFilters(r, { ...filters, [dimension]: value }),
      ).length;

    return {
      status: {
        all: countFor("status", "all"),
        approved: countFor("status", "approved"),
        pending: countFor("status", "pending"),
        rejected: countFor("status", "rejected"),
        sent: countFor("status", "sent"),
      },
      platform: {
        all: countFor("platform", "all"),
        instagram: countFor("platform", "instagram"),
        facebook: countFor("platform", "facebook"),
        messenger: countFor("platform", "messenger"),
        linkedin: countFor("platform", "linkedin"),
        google_my_business: countFor("platform", "google_my_business"),
      },
      sentiment: {
        all: countFor("sentiment", "all"),
        positive: countFor("sentiment", "positive"),
        neutral: countFor("sentiment", "neutral"),
        negative: countFor("sentiment", "negative"),
      },
      message_type: {
        all: countFor("message_type", "all"),
        question: countFor("message_type", "question"),
        complaint: countFor("message_type", "complaint"),
        compliment: countFor("message_type", "compliment"),
        lead: countFor("message_type", "lead"),
        spam: countFor("message_type", "spam"),
        other: countFor("message_type", "other"),
      },
      source_channel: {
        all: countFor("source_channel", "all"),
        comment: countFor("source_channel", "comment"),
        direct: countFor("source_channel", "direct"),
        messenger: countFor("source_channel", "messenger"),
        story_reply: countFor("source_channel", "story_reply"),
      },
      tag: {
        all: countFor("tag", "all"),
        ...Object.fromEntries(
          customTags.map((ct) => [
            String(ct.id),
            countFor("tag", String(ct.id)),
          ]),
        ),
      } as Record<string, number>,
    };
  }, [responses, filters, customTags]);

  return (
    <PageLayout
      title={t("Auto-Responder IA")}
      subtitle={t(
        "Configure respostas inteligentes para comentários e mensagens nas suas redes sociais com IA.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2 py-4"
      loading={loading}
      skeleton={
        <AutoResponderSkeleton
          activeTab={activeTab}
          responsesView={responsesView}
        />
      }
      actions={
        <div className="flex items-center gap-2">
          <Button
            onClick={handleManualMonitor}
            disabled={loading || monitorLoading}
            isLoading={monitorLoading}
            variant="outline"
            size="sm"
            className={HEADER_SECONDARY_BUTTON}
            title={t("Verificar novas mensagens agora")}
            icon={<Zap className={HEADER_SECONDARY_ICON} />}
          >
            <span className="hidden sm:inline text-xs">
              {t("Verificar Agora")}
            </span>
            <span className="sm:hidden text-xs">{t("Verificar")}</span>
          </Button>
          <Button
            onClick={loadAutoResponderData}
            disabled={loading}
            variant="outline"
            size="sm"
            className={HEADER_SECONDARY_BUTTON}
            title={t("Atualizar lista")}
            icon={
              <RefreshCw
                className={cn(HEADER_SECONDARY_ICON, loading && "animate-spin")}
              />
            }
          >
            <span className="text-xs">{t("Atualizar")}</span>
          </Button>
        </div>
      }
    >
      {/* Wrapper único: PageLayout aplica space-y entre children diretos, então
          agrupamos navbar+decorações+conteúdo num só filho para controlar o
          gap manualmente (mesma solução usada em EmpathyMapping.tsx). */}
      <div>
        {/* Navbar de navegação - controle segmentado próprio (mesmo padrão da
            página Mapa de Empatia: preenchimento sólido no item ativo) */}
        <div className="sticky top-0 z-30 -mt-4 -mx-2 bg-white/95 backdrop-blur-md border-b border-purple-200">
          <div className="w-full flex gap-1 bg-gray-100 p-1">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[12px] font-bold uppercase tracking-wide transition-all duration-150 ${
                    isActive
                      ? "bg-purple-600 text-white shadow-xs"
                      : "bg-white text-gray-600 border border-gray-200 shadow-xs hover:text-purple-700 hover:border-purple-300 cursor-pointer"
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Filter Bar - aba Histórico (mesmo padrão da página Postagens) */}
          {activeTab === "historico" && (
            <div className="w-full px-4 py-2 flex flex-col gap-2 border-t border-purple-100">
              <div className="flex flex-row flex-wrap gap-2 items-center">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    type="text"
                    placeholder={t("Buscar por conteúdo ou gatilho...")}
                    value={filters.query || ""}
                    onChange={(e) =>
                      setFilters({ ...filters, query: e.target.value })
                    }
                    inputSize="sm"
                    icon={<Search className="h-4 w-4 text-gray-400" />}
                    className="w-full h-8! sm:h-9!"
                  />
                </div>

                <div className="w-[230px] shrink-0">
                  <Select
                    size="sm"
                    showSelectedCount
                    className="h-8! sm:h-9!"
                    value={filters.status}
                    onChange={(v) => setFilters({ ...filters, status: v })}
                    options={[
                      {
                        value: "all",
                        label: t("Filtro de Status"),
                        icon: <ListFilter />,
                        count: filterCounts.status.all,
                      },
                      {
                        value: "approved",
                        label: t("Aprovados"),
                        icon: <CheckCircle />,
                        count: filterCounts.status.approved,
                      },
                      {
                        value: "pending",
                        label: t("Pendentes"),
                        icon: <Clock />,
                        count: filterCounts.status.pending,
                      },
                      {
                        value: "rejected",
                        label: t("Rejeitados"),
                        icon: <XCircle />,
                        count: filterCounts.status.rejected,
                      },
                      {
                        value: "sent",
                        label: t("Enviados"),
                        icon: <Send />,
                        count: filterCounts.status.sent,
                      },
                    ]}
                  />
                </div>
                <div className="w-[250px] shrink-0">
                  <Select
                    size="sm"
                    showSelectedCount
                    className="h-8! sm:h-9!"
                    value={filters.platform}
                    onChange={(v) => setFilters({ ...filters, platform: v })}
                    options={[
                      {
                        value: "all",
                        label: t("Todas as Redes"),
                        icon: <ListFilter />,
                        count: filterCounts.platform.all,
                      },
                      {
                        value: "instagram",
                        label: "Instagram",
                        icon: (
                          <span
                            className={getPlatformConfig("instagram").color}
                          >
                            <PlatformIcon platform="instagram" />
                          </span>
                        ),
                        count: filterCounts.platform.instagram,
                      },
                      {
                        value: "facebook",
                        label: "Facebook",
                        icon: (
                          <span className={getPlatformConfig("facebook").color}>
                            <PlatformIcon platform="facebook" />
                          </span>
                        ),
                        count: filterCounts.platform.facebook,
                      },
                      {
                        value: "messenger",
                        label: "Messenger",
                        icon: (
                          <span
                            className={getPlatformConfig("messenger").color}
                          >
                            <PlatformIcon platform="messenger" />
                          </span>
                        ),
                        count: filterCounts.platform.messenger,
                      },
                      {
                        value: "linkedin",
                        label: "LinkedIn",
                        icon: (
                          <span className={getPlatformConfig("linkedin").color}>
                            <PlatformIcon platform="linkedin" />
                          </span>
                        ),
                        count: filterCounts.platform.linkedin,
                      },
                      {
                        value: "google_my_business",
                        label: "Google My Business",
                        icon: (
                          <span
                            className={
                              getPlatformConfig("google_my_business").color
                            }
                          >
                            <PlatformIcon platform="google_my_business" />
                          </span>
                        ),
                        count: filterCounts.platform.google_my_business,
                      },
                    ]}
                  />
                </div>

                {/* Sort Toggle */}
                <div className="flex items-center gap-1 bg-gray-50 border border-purple-200 rounded-xl p-1 h-8 sm:h-9 shrink-0">
                  <button
                    onClick={() => setSortOrder("newest")}
                    className={cn(
                      "w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center transition-all",
                      sortOrder === "newest"
                        ? "bg-white shadow-sm text-purple-600 border border-purple-200"
                        : "text-gray-400 hover:text-purple-500",
                    )}
                    title={t("Mais recentes no topo")}
                  >
                    <ArrowDown size={16} />
                  </button>
                  <button
                    onClick={() => setSortOrder("oldest")}
                    className={cn(
                      "w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center transition-all",
                      sortOrder === "oldest"
                        ? "bg-white shadow-sm text-purple-600 border border-purple-200"
                        : "text-gray-400 hover:text-purple-500",
                    )}
                    title={t("Mais antigas no topo")}
                  >
                    <ArrowUp size={16} />
                  </button>
                </div>

                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-gray-50 border border-purple-200 rounded-xl p-1 h-8 sm:h-9 shrink-0">
                  <button
                    onClick={() => setResponsesView("list")}
                    className={cn(
                      "w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center transition-all",
                      responsesView === "list"
                        ? "bg-white shadow-sm text-purple-600 border border-purple-200"
                        : "text-gray-400 hover:text-purple-500",
                    )}
                    title={t("Visualização em lista")}
                  >
                    <List size={16} />
                  </button>
                  <button
                    onClick={() => setResponsesView("kanban")}
                    className={cn(
                      "w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center transition-all",
                      responsesView === "kanban"
                        ? "bg-white shadow-sm text-purple-600 border border-purple-200"
                        : "text-gray-400 hover:text-purple-500",
                    )}
                    title={t("Visualização Kanban")}
                  >
                    <Kanban size={16} />
                  </button>
                </div>
              </div>

              <div className="flex flex-row flex-wrap gap-2 items-center">
                <div className="w-[210px] shrink-0">
                  <Select
                    size="sm"
                    showSelectedCount
                    className="h-8! sm:h-9!"
                    value={filters.sentiment}
                    onChange={(v) => setFilters({ ...filters, sentiment: v })}
                    options={[
                      {
                        value: "all",
                        label: t("Sentimento"),
                        icon: <ListFilter />,
                        count: filterCounts.sentiment.all,
                      },
                      {
                        value: "positive",
                        label: t("Positivo"),
                        icon: <Smile />,
                        count: filterCounts.sentiment.positive,
                      },
                      {
                        value: "neutral",
                        label: t("Neutro"),
                        icon: <Meh />,
                        count: filterCounts.sentiment.neutral,
                      },
                      {
                        value: "negative",
                        label: t("Negativo"),
                        icon: <Frown />,
                        count: filterCounts.sentiment.negative,
                      },
                    ]}
                  />
                </div>
                <div className="w-[190px] shrink-0">
                  <Select
                    size="sm"
                    showSelectedCount
                    className="h-8! sm:h-9!"
                    value={filters.message_type}
                    onChange={(v) =>
                      setFilters({ ...filters, message_type: v })
                    }
                    options={[
                      {
                        value: "all",
                        label: t("Tipo"),
                        icon: <ListFilter />,
                        count: filterCounts.message_type.all,
                      },
                      {
                        value: "question",
                        label: t("Pergunta"),
                        icon: <HelpCircle />,
                        count: filterCounts.message_type.question,
                      },
                      {
                        value: "complaint",
                        label: t("Reclamação"),
                        icon: <AlertTriangle />,
                        count: filterCounts.message_type.complaint,
                      },
                      {
                        value: "compliment",
                        label: t("Elogio"),
                        icon: <ThumbsUp />,
                        count: filterCounts.message_type.compliment,
                      },
                      {
                        value: "lead",
                        label: "Lead",
                        icon: <UserPlus />,
                        count: filterCounts.message_type.lead,
                      },
                      {
                        value: "spam",
                        label: "Spam",
                        icon: <ShieldAlert />,
                        count: filterCounts.message_type.spam,
                      },
                      {
                        value: "other",
                        label: t("Outro"),
                        count: filterCounts.message_type.other,
                        icon: <MoreHorizontal />,
                      },
                    ]}
                  />
                </div>
                <div className="w-[190px] shrink-0">
                  <Select
                    size="sm"
                    showSelectedCount
                    className="h-8! sm:h-9!"
                    value={filters.source_channel}
                    onChange={(v) =>
                      setFilters({ ...filters, source_channel: v })
                    }
                    options={[
                      {
                        value: "all",
                        label: t("Canal"),
                        icon: <ListFilter />,
                        count: filterCounts.source_channel.all,
                      },
                      {
                        value: "comment",
                        label: t("Comentário"),
                        icon: <MessageSquare />,
                        count: filterCounts.source_channel.comment,
                      },
                      {
                        value: "direct",
                        label: "Direct",
                        icon: <Mail />,
                        count: filterCounts.source_channel.direct,
                      },
                      {
                        value: "messenger",
                        label: "Messenger",
                        icon: <MessageCircle />,
                        count: filterCounts.source_channel.messenger,
                      },
                      {
                        value: "story_reply",
                        label: "Story Reply",
                        icon: <Camera />,
                        count: filterCounts.source_channel.story_reply,
                      },
                    ]}
                  />
                </div>
                {customTags.length > 0 && (
                  <div className="w-[230px] shrink-0">
                    <Select
                      size="sm"
                      showSelectedCount
                      className="h-8! sm:h-9!"
                      value={filters.tag}
                      onChange={(v) => setFilters({ ...filters, tag: v })}
                      options={[
                        {
                          value: "all",
                          label: t("Tag"),
                          icon: <Tag />,
                          count: filterCounts.tag.all,
                        },
                        ...customTags.map((ct) => ({
                          value: String(ct.id),
                          label: ct.name,
                          icon: (
                            <span
                              className="h-2.5 w-2.5 rounded-full inline-block"
                              style={{ backgroundColor: ct.color }}
                            />
                          ),
                          count: filterCounts.tag[String(ct.id)] ?? 0,
                        })),
                      ]}
                    />
                  </div>
                )}
                {(filters.query ||
                  filters.status !== "all" ||
                  filters.platform !== "all" ||
                  filters.sentiment !== "all" ||
                  filters.message_type !== "all" ||
                  filters.source_channel !== "all" ||
                  filters.tag !== "all") && (
                  <div className="shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFilters({
                          ...filters,
                          query: "",
                          status: "all",
                          platform: "all",
                          sentiment: "all",
                          message_type: "all",
                          source_channel: "all",
                          tag: "all",
                        })
                      }
                      className="rounded-xl h-8! sm:h-9! w-8! sm:w-9! p-0! border! border-purple-200! bg-gray-50! text-gray-500! hover:border-red-300! hover:bg-red-50! hover:text-red-600! shadow-none!"
                      title={t("Limpar filtros")}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Background Decorations */}
        <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] bg-purple-100/30 blur-[120px] rounded-full" />
          <div className="absolute top-[20%] left-[-5%] w-[30%] h-[30%] bg-blue-100/20 blur-[100px] rounded-full" />
        </div>

        <div className="space-y-2 w-full mt-2">
          {error && activeTab !== "conhecimento" && (
            <Alert
              variant="error"
              title={t("Não foi possível carregar os dados")}
              dismissible
              onDismiss={() => setError("")}
              className="bg-red-50 border-red-100 my-2"
            >
              {error}
            </Alert>
          )}
          {/* Monitoramento Tab */}
          {activeTab === "monitoramento" && !stats && !loading && (
            <div className="bg-white/50 border border-purple-200/50 rounded-3xl py-12 sm:py-16 shadow-xs flex flex-col items-center justify-center">
              <EmptyState
                title={t("Não foi possível carregar os dados")}
                description={t(
                  "Verifique sua conexão e tente novamente em instantes.",
                )}
                icon={
                  <AlertCircle className="h-16 w-16 sm:h-20 sm:w-20 text-gray-400" />
                }
                action={
                  <Button
                    variant="primary"
                    icon={
                      <RefreshCw
                        className={cn("h-4 w-4", loading && "animate-spin")}
                      />
                    }
                    onClick={() => loadAutoResponderData()}
                  >
                    {t("Tentar novamente")}
                  </Button>
                }
              />
            </div>
          )}
          {activeTab === "monitoramento" && stats && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap h-[52px] bg-white/70 backdrop-blur-xs border border-purple-200 rounded-2xl px-4 sm:px-5 py-2.5 shadow-xs">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">
                    {t("Monitoramento")}
                  </h2>
                </div>
                {lastRefreshedAt && (
                  <span className="text-xs text-gray-400 font-medium">
                    {t("Atualizado")} {formatRelativeTime(lastRefreshedAt, t)}
                  </span>
                )}
              </div>

              {/* Metrics Grid - mesmo padrão de KPI card da página Anúncios */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-1">
                {(() => {
                  const KPI_COLORS: Record<
                    string,
                    {
                      border: string;
                      text: string;
                      iconBg: string;
                      iconText: string;
                      hoverGlow: string;
                      subBg: string;
                      subText: string;
                    }
                  > = {
                    purple: {
                      border: "border-purple-200",
                      text: "text-purple-700",
                      iconBg: "bg-purple-100",
                      iconText: "text-purple-600",
                      hoverGlow:
                        "hover:shadow-[0_4px_24px_rgba(168,85,247,0.18)]",
                      subBg: "bg-purple-50",
                      subText: "text-purple-500",
                    },
                    orange: {
                      border: "border-orange-200",
                      text: "text-orange-700",
                      iconBg: "bg-orange-100",
                      iconText: "text-orange-600",
                      hoverGlow:
                        "hover:shadow-[0_4px_24px_rgba(249,115,22,0.18)]",
                      subBg: "bg-orange-50",
                      subText: "text-orange-500",
                    },
                    emerald: {
                      border: "border-emerald-200",
                      text: "text-emerald-700",
                      iconBg: "bg-emerald-100",
                      iconText: "text-emerald-600",
                      hoverGlow:
                        "hover:shadow-[0_4px_24px_rgba(16,185,129,0.18)]",
                      subBg: "bg-emerald-50",
                      subText: "text-emerald-500",
                    },
                    blue: {
                      border: "border-blue-200",
                      text: "text-blue-700",
                      iconBg: "bg-blue-100",
                      iconText: "text-blue-600",
                      hoverGlow:
                        "hover:shadow-[0_4px_24px_rgba(59,130,246,0.18)]",
                      subBg: "bg-blue-50",
                      subText: "text-blue-500",
                    },
                  };

                  return [
                    {
                      icon: MessageSquare,
                      color: "purple",
                      label: t("Interações Totais"),
                      value: String(stats.total_responses),
                      sub: t("Respostas geradas pela IA"),
                      onClick: () => {
                        setActiveTab("historico");
                        setFilters({ ...filters, status: "all" });
                      },
                    },
                    {
                      icon: Clock,
                      color: "orange",
                      label: t("Pendentes"),
                      value: String(stats.pending_approvals),
                      sub: t("Aguardando sua revisão"),
                      onClick: () => {
                        setActiveTab("historico");
                        setFilters({ ...filters, status: "pending" });
                      },
                    },
                    {
                      icon: Zap,
                      color: "emerald",
                      label: t("Taxa de Aprovação"),
                      value: `${(stats.auto_approval_rate * 100).toFixed(0)}%`,
                      sub: t("Confiança média da IA"),
                    },
                    {
                      icon: TrendingUp,
                      color: "blue",
                      label: t("Tempo de Resposta"),
                      value: formatResponseTime(stats.response_time_avg),
                      sub: t("Média por interação"),
                    },
                  ].map(({ icon: Icon, color, label, value, sub, onClick }) => {
                    const s = KPI_COLORS[color];
                    return (
                      <div
                        key={label}
                        onClick={onClick}
                        className={cn(
                          "relative bg-white rounded-xl border p-4 shadow-xs transition-all duration-200 flex flex-col gap-3 overflow-hidden group",
                          s.border,
                          s.hoverGlow,
                          onClick
                            ? "cursor-pointer hover:-translate-y-0.5"
                            : "hover:-translate-y-0.5",
                        )}
                      >
                        {/* Colored top accent bar */}
                        <div
                          className={cn(
                            "absolute top-0 left-0 right-0 h-[3px]",
                            s.iconBg,
                          )}
                        />

                        <div className="flex items-start justify-between pt-0.5">
                          <div className="flex flex-col min-w-0">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 truncate">
                              {label}
                            </p>
                            <h3
                              className={cn(
                                "text-2xl font-black tracking-tight leading-none",
                                s.text,
                              )}
                            >
                              {value}
                            </h3>
                          </div>
                          <div
                            className={cn(
                              "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110",
                              s.iconBg,
                              s.iconText,
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                        </div>

                        <div
                          className={cn(
                            "flex items-center justify-between gap-1 text-[10px] font-semibold px-2 py-1.5 rounded-lg",
                            s.subBg,
                            s.subText,
                          )}
                        >
                          <div className="flex items-center gap-1 truncate">
                            <BarChart3 className="h-3 w-3 shrink-0" />
                            <span className="truncate">{sub}</span>
                          </div>
                          {onClick && (
                            <ArrowRight className="h-3 w-3 shrink-0 opacity-0 -translate-x-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0" />
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {/* Recent Activity */}
                <div className="lg:col-span-2 flex flex-col bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-3 sm:p-4">
                  <SectionHeader
                    icon={<Activity className="w-5 h-5" />}
                    title={t("Atividade Recente")}
                    subtitle={t("Últimas interações respondidas pela IA")}
                    action={
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-lg h-8! min-h-[32px]! py-1! border! border-purple-300! text-purple-700! bg-purple-50! hover:bg-purple-100! hover:text-purple-700! shadow-xs! hover:shadow-xs! shrink-0"
                        onClick={() => setActiveTab("historico")}
                      >
                        <span className="text-xs">{t("Ver Tudo")}</span>
                      </Button>
                    }
                  />

                  <div className="flex-1 flex flex-col space-y-1.5">
                    {(filteredResponses || []).filter(
                      (r) => !isTextlessResponse(r),
                    ).length > 0 ? (
                      (filteredResponses || [])
                        .filter((r) => !isTextlessResponse(r))
                        .slice(0, 5)
                        .map((response) => {
                          const status = getStatusConfig(response.status);
                          const StatusIcon = status.icon;
                          const variantClasses = {
                            emerald:
                              "bg-emerald-50 text-emerald-600 border-emerald-100/50",
                            amber:
                              "bg-amber-50 text-amber-600 border-amber-100/50",
                            red: "bg-red-50 text-red-600 border-red-100/50",
                            blue: "bg-blue-50 text-blue-600 border-blue-100/50",
                            purple:
                              "bg-purple-50 text-purple-600 border-purple-200/50",
                            gray: "bg-gray-50 text-gray-600 border-purple-200/50",
                          };
                          const activeClass =
                            variantClasses[
                              status.variant as keyof typeof variantClasses
                            ] || variantClasses.gray;

                          const platformConfig = getPlatformConfig(
                            response.platform,
                          );

                          return (
                            <Card
                              key={response.id}
                              className="group hover:border-purple-300! hover:shadow-xl hover:shadow-purple-500/5 transition-all duration-300 border! border-purple-200! p-0!"
                            >
                              <div className="flex items-center gap-2 px-1.5 py-1">
                                <div
                                  className={cn(
                                    "w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border transition-all",
                                    platformConfig.bgColor,
                                    platformConfig.color,
                                    platformConfig.borderColor,
                                  )}
                                >
                                  <PlatformIcon
                                    platform={response.platform}
                                    size={24}
                                  />
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-0.5">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-[11px] font-black text-gray-900 uppercase tracking-widest">
                                        {getPlatformName(response.platform)}
                                      </span>
                                      <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">
                                        {response.created_at
                                          ? new Date(
                                              response.created_at,
                                            ).toLocaleString("pt-BR", {
                                              day: "2-digit",
                                              month: "2-digit",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })
                                          : ""}
                                      </span>
                                    </div>
                                    <div
                                      className={cn(
                                        "px-2 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-widest flex items-center gap-1 shrink-0",
                                        activeClass,
                                      )}
                                    >
                                      <StatusIcon size={10} />
                                      {status.label}
                                    </div>
                                  </div>

                                  <div className="text-[11px] text-gray-600 line-clamp-1">
                                    <span className="text-gray-400 italic">
                                      "{response.trigger}"
                                    </span>{" "}
                                    <span className="text-purple-900 font-bold">
                                      → {response.response}
                                    </span>
                                  </div>
                                  {(response.sentiment ||
                                    response.message_type) && (
                                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                      {response.sentiment && (
                                        <span
                                          className={cn(
                                            "text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border",
                                            response.sentiment === "positive" &&
                                              "bg-emerald-50 text-emerald-600 border-emerald-200",
                                            response.sentiment === "negative" &&
                                              "bg-red-50 text-red-600 border-red-200",
                                            response.sentiment === "neutral" &&
                                              "bg-gray-50 text-gray-500 border-gray-200",
                                          )}
                                        >
                                          {response.sentiment === "positive"
                                            ? "😊 positivo"
                                            : response.sentiment === "negative"
                                              ? "😟 negativo"
                                              : "😐 neutro"}
                                        </span>
                                      )}
                                      {response.message_type && (
                                        <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border bg-purple-50 text-purple-600 border-purple-200">
                                          {MESSAGE_TYPE_PT[
                                            response.message_type
                                          ] ?? response.message_type}
                                        </span>
                                      )}
                                      {response.metadata?.post_id && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            fetchOriginPost(response.id);
                                          }}
                                          className="text-[9px] font-bold text-blue-500 hover:text-blue-700 underline ml-auto whitespace-nowrap"
                                        >
                                          Ver post origem
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Card>
                          );
                        })
                    ) : (
                      <Card className="flex-1 border-dashed! border-2! border-purple-200! bg-[#fafafa]/50 flex flex-col items-center justify-center text-center p-12">
                        <div className="relative w-16 h-16 mb-6 mx-auto">
                          <div className="absolute inset-0 bg-purple-200 blur-3xl opacity-50 rounded-full"></div>
                          <div className="relative w-full h-full rounded-full bg-white border border-purple-200 shadow-xl shadow-purple-500/10 flex items-center justify-center">
                            <Activity className="h-8 w-8 text-purple-500" />
                          </div>
                        </div>
                        <h4 className="text-[13px] font-black text-gray-900 uppercase tracking-[0.2em] mb-2 mx-auto">
                          {t("Nenhuma atividade registrada")}
                        </h4>
                        <p className="text-[11px] text-gray-500 max-w-[240px] leading-relaxed mx-auto">
                          As interações recentes com seus leads por IA
                          aparecerão neste painel.
                        </p>
                      </Card>
                    )}
                  </div>
                </div>

                {/* Quick Test */}
                <div className="flex flex-col bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-3 sm:p-4">
                  <SectionHeader
                    icon={<Zap className="w-5 h-5" />}
                    title={t("Teste Rápido")}
                    subtitle={t("Simule uma resposta da IA")}
                  />

                  <div>
                    <div className="space-y-2.5 flex-1 flex flex-col">
                      <div className="p-2 bg-amber-50 border border-amber-100 rounded-xl flex gap-2 items-start">
                        <AlertCircle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-[10px] text-amber-800 leading-snug">
                          {t(
                            "Use esta ferramenta para validar como a IA responderia a um comentário específico no seu contexto atual.",
                          )}
                        </p>
                      </div>

                      <div className="space-y-2 flex-1">
                        <Select
                          label={t("Plataforma Alvo")}
                          value={testingReply.platform}
                          onChange={(value) =>
                            setTestingReply({
                              ...testingReply,
                              platform: value,
                            })
                          }
                          options={[
                            { value: "instagram", label: "Instagram Business" },
                            { value: "facebook", label: "Facebook Page" },
                            { value: "messenger", label: "Messenger Direct" },
                            { value: "linkedin", label: "LinkedIn Enterprise" },
                            {
                              value: "google_my_business",
                              label: "Google Meu Negócio",
                            },
                          ]}
                        />
                        <Input
                          label={t("ID da Mensagem original")}
                          placeholder={t("ex: comment_123456...")}
                          value={testingReply.id}
                          onChange={(e) =>
                            setTestingReply({
                              ...testingReply,
                              id: e.target.value,
                            })
                          }
                        />
                        <Input
                          label={t("Simular Texto do Cliente")}
                          placeholder={t("Como funciona o produto X?")}
                          value={testingReply.text}
                          onChange={(e) =>
                            setTestingReply({
                              ...testingReply,
                              text: e.target.value,
                            })
                          }
                        />
                      </div>
                      <Button
                        className="w-full h-9 font-bold text-xs uppercase tracking-widest shadow-lg shadow-purple-500/20 mt-auto"
                        onClick={replyWithAI}
                        disabled={testLoading}
                        icon={<Bot className="h-4 w-4" />}
                      >
                        {testLoading ? t("Gerando...") : t("Gerar Resposta IA")}
                      </Button>

                      {testResult && (
                        <div className="mt-3 p-3 rounded-2xl bg-purple-50 border border-purple-100 space-y-2">
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                testResult.sentiment === "positive"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : testResult.sentiment === "negative"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {testResult.sentiment}
                            </span>
                            <span className="text-[9px] font-bold text-purple-400 uppercase tracking-wider">
                              {MESSAGE_TYPE_PT[testResult.message_type] ??
                                testResult.message_type}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-700 leading-relaxed">
                            {testResult.response}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sentiment Panel */}
              {sentimentStats && (
                <div className="bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-4 sm:p-5">
                  <SectionHeader
                    icon={<Activity className="w-5 h-5" />}
                    title={t("Análise de Sentimento")}
                    subtitle={t("Distribuição de sentimento, tipo e canal")}
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Sentiment distribution */}
                    <div className="relative overflow-hidden bg-white border border-purple-200 rounded-4xl p-5 shadow-xl shadow-gray-200/10 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/5 group">
                      <div className="absolute top-0 left-0 w-full h-1 bg-linear-to-r from-blue-500/10 to-indigo-500/10" />
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2.5 rounded-xl bg-linear-to-br from-blue-500/10 to-indigo-500/10 text-blue-600 border border-blue-200 group-hover:scale-110 transition-transform">
                          <Activity className="w-4 h-4" />
                        </div>
                        <div className="flex-1 flex items-center justify-between gap-2">
                          <h4 className="font-black text-xs text-gray-900 uppercase tracking-widest">
                            {t("Sentimento")}
                          </h4>
                          <span className="text-[9px] font-bold text-gray-400 tabular-nums">
                            {t("{count} registros", {
                              count: String(
                                (sentimentStats.sentiment.positive ?? 0) +
                                  (sentimentStats.sentiment.neutral ?? 0) +
                                  (sentimentStats.sentiment.negative ?? 0),
                              ),
                            })}
                          </span>
                        </div>
                      </div>
                      <ResponsiveContainer width="100%" height={140}>
                        <BarChart
                          data={[
                            {
                              name: "Positivo",
                              value: sentimentStats.sentiment.positive ?? 0,
                              fill: "#10b981",
                            },
                            {
                              name: "Neutro",
                              value: sentimentStats.sentiment.neutral ?? 0,
                              fill: "#6b7280",
                            },
                            {
                              name: "Negativo",
                              value: sentimentStats.sentiment.negative ?? 0,
                              fill: "#ef4444",
                            },
                          ]}
                          barSize={28}
                          margin={{ top: 16 }}
                        >
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 10, fontWeight: 700 }}
                            axisLine={{ stroke: "#e5e7eb" }}
                            tickLine={false}
                          />
                          <YAxis hide />
                          <Tooltip formatter={(v) => [v, "mensagens"]} />
                          <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                            <LabelList
                              dataKey="value"
                              position="top"
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                fill: "#374151",
                              }}
                            />
                            {[
                              { fill: "#10b981" },
                              { fill: "#6b7280" },
                              { fill: "#ef4444" },
                            ].map((entry, i) => (
                              <Cell key={i} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Message type distribution */}
                    <div className="relative overflow-hidden bg-white border border-purple-200 rounded-4xl p-5 shadow-xl shadow-gray-200/10 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/5 group">
                      <div className="absolute top-0 left-0 w-full h-1 bg-linear-to-r from-purple-500/10 to-pink-500/10" />
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2.5 rounded-xl bg-linear-to-br from-purple-500/10 to-pink-500/10 text-purple-600 border border-purple-200 group-hover:scale-110 transition-transform">
                          <Tag className="w-4 h-4" />
                        </div>
                        <div className="flex-1 flex items-center justify-between gap-2">
                          <h4 className="font-black text-xs text-gray-900 uppercase tracking-widest">
                            {t("Tipo de Mensagem")}
                          </h4>
                          <span className="text-[9px] font-bold text-gray-400 tabular-nums">
                            {t("{count} registros", {
                              count: String(
                                Object.values(
                                  sentimentStats.message_types,
                                ).reduce((sum, v) => sum + v, 0),
                              ),
                            })}
                          </span>
                        </div>
                      </div>
                      <ResponsiveContainer width="100%" height={140}>
                        <BarChart
                          data={Object.entries(
                            sentimentStats.message_types,
                          ).map(([k, v]) => ({
                            name:
                              {
                                question: "Pergunta",
                                complaint: "Reclamação",
                                compliment: "Elogio",
                                spam: "Spam",
                                lead: "Lead",
                                other: "Outro",
                              }[k] ?? k,
                            value: v,
                          }))}
                          barSize={18}
                          margin={{ top: 16 }}
                        >
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 9, fontWeight: 700 }}
                            axisLine={{ stroke: "#e5e7eb" }}
                            tickLine={false}
                          />
                          <YAxis hide />
                          <Tooltip formatter={(v) => [v, "mensagens"]} />
                          <Bar
                            dataKey="value"
                            fill="#8b5cf6"
                            radius={[6, 6, 0, 0]}
                          >
                            <LabelList
                              dataKey="value"
                              position="top"
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                fill: "#374151",
                              }}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Channel distribution */}
                    <div className="relative overflow-hidden bg-white border border-purple-200 rounded-4xl p-5 shadow-xl shadow-gray-200/10 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/5 group">
                      <div className="absolute top-0 left-0 w-full h-1 bg-linear-to-r from-emerald-500/10 to-teal-500/10" />
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2.5 rounded-xl bg-linear-to-br from-emerald-500/10 to-teal-500/10 text-emerald-600 border border-emerald-200 group-hover:scale-110 transition-transform">
                          <MessageSquare className="w-4 h-4" />
                        </div>
                        <div className="flex-1 flex items-center justify-between gap-2">
                          <h4 className="font-black text-xs text-gray-900 uppercase tracking-widest">
                            {t("Canal de Origem")}
                          </h4>
                          <span className="text-[9px] font-bold text-gray-400 tabular-nums">
                            {t("{count} registros", {
                              count: String(
                                Object.values(sentimentStats.channels).reduce(
                                  (sum, v) => sum + v,
                                  0,
                                ),
                              ),
                            })}
                          </span>
                        </div>
                      </div>
                      <ResponsiveContainer width="100%" height={140}>
                        <BarChart
                          data={Object.entries(sentimentStats.channels).map(
                            ([k, v]) => ({
                              name:
                                {
                                  comment: "Comentário",
                                  direct: "Direct",
                                  messenger: "Messenger",
                                  story_reply: "Story",
                                }[k] ?? k,
                              value: v,
                            }),
                          )}
                          barSize={22}
                          margin={{ top: 16 }}
                        >
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 9, fontWeight: 700 }}
                            axisLine={{ stroke: "#e5e7eb" }}
                            tickLine={false}
                          />
                          <YAxis hide />
                          <Tooltip formatter={(v) => [v, "mensagens"]} />
                          <Bar
                            dataKey="value"
                            fill="#3b82f6"
                            radius={[6, 6, 0, 0]}
                          >
                            <LabelList
                              dataKey="value"
                              position="top"
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                fill: "#374151",
                              }}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Responses Tab */}
          {activeTab === "historico" && (
            <div className="space-y-2">
              {/* Barra de ações em lote — aparece quando há seleção ou pendentes visíveis */}
              {(() => {
                const pendingVisible = filteredResponses.filter(
                  (r) => r.status === "pending",
                );
                const anySelected = selectedIds.size > 0;
                if (!anySelected && pendingVisible.length === 0) return null;
                return (
                  <div className="flex items-center gap-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl shadow-xs">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-purple-600 cursor-pointer"
                      checked={
                        pendingVisible.length > 0 &&
                        pendingVisible.every((r) => selectedIds.has(r.id))
                      }
                      onChange={() =>
                        selectAll(pendingVisible.map((r) => r.id))
                      }
                      title={t("Selecionar todos os pendentes")}
                    />
                    <span className="text-xs font-bold text-amber-800">
                      {anySelected
                        ? `${selectedIds.size} ${t("selecionado(s)")}`
                        : `${pendingVisible.length} ${t("pendente(s)")}`}
                    </span>
                    <div className="flex-1" />
                    {anySelected && (
                      <>
                        <Button
                          size="sm"
                          isLoading={bulkLoading}
                          onClick={handleBulkApprove}
                          disabled={bulkLoading}
                          icon={<CheckCircle className="h-3.5 w-3.5" />}
                          className="rounded-lg h-8! min-h-[32px]! py-1! border! border-emerald-300! text-emerald-700! bg-emerald-50! hover:bg-emerald-100! hover:text-emerald-700! shadow-xs! hover:shadow-xs!"
                        >
                          <span className="text-xs">
                            {t("Aprovar")} {selectedIds.size}
                          </span>
                        </Button>
                        <Button
                          size="sm"
                          isLoading={bulkLoading}
                          onClick={handleBulkReject}
                          disabled={bulkLoading}
                          icon={<XCircle className="h-3.5 w-3.5" />}
                          className="rounded-lg h-8! min-h-[32px]! py-1! border! border-red-300! text-red-700! bg-red-50! hover:bg-red-100! hover:text-red-700! shadow-xs! hover:shadow-xs!"
                        >
                          <span className="text-xs">
                            {t("Rejeitar")} {selectedIds.size}
                          </span>
                        </Button>
                        <button
                          onClick={() => setSelectedIds(new Set())}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Kanban View */}
              {responsesView === "kanban" && (
                <AutoResponderKanban
                  responses={filteredResponses}
                  onApprove={handleApproveResponse}
                  onReject={(id) =>
                    handleRejectResponse(id, "Rejeitado via Kanban")
                  }
                  onSend={handleSendResponse}
                  onCardClick={(r) => openResponseModal(r)}
                  onDataChange={loadAutoResponderData}
                  customTags={customTags}
                />
              )}

              {/* Responses List */}
              {responsesView === "list" && (
                <div className="space-y-2">
                  {filteredResponses.length > 0 ? (
                    filteredResponses.map((response) => {
                      const status = getStatusConfig(response.status);
                      const StatusIcon = status.icon;
                      return (
                        <Card
                          key={response.id}
                          className={cn(
                            "group hover:border-purple-300! hover:shadow-xl hover:shadow-purple-500/5 transition-all duration-300 p-0! overflow-hidden border-purple-200! rounded-3xl bg-white",
                            selectedIds.has(response.id) &&
                              "ring-2 ring-purple-400 border-purple-300!",
                          )}
                        >
                          <div className="p-3 flex flex-col gap-2.5">
                            {/* Header: plataforma, status, confiança */}
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="flex items-center gap-3 min-w-0">
                                {response.status === "pending" && (
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-purple-600 cursor-pointer shrink-0"
                                    checked={selectedIds.has(response.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleSelect(response.id)}
                                  />
                                )}
                                <div
                                  className={cn(
                                    "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-all",
                                    getPlatformConfig(response.platform)
                                      .bgColor,
                                    getPlatformConfig(response.platform).color,
                                    getPlatformConfig(response.platform)
                                      .borderColor,
                                  )}
                                >
                                  <PlatformIcon
                                    platform={response.platform}
                                    size={20}
                                  />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[12px] font-black text-gray-900 uppercase tracking-widest truncate">
                                    {getPlatformName(response.platform)}
                                  </p>
                                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                    {response.created_at
                                      ? new Date(
                                          response.created_at,
                                        ).toLocaleString("pt-BR", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })
                                      : ""}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-1.5 flex-wrap justify-end shrink-0">
                                {response.sentiment && (
                                  <span
                                    className={cn(
                                      "text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full border",
                                      response.sentiment === "positive" &&
                                        "bg-emerald-50 text-emerald-600 border-emerald-200",
                                      response.sentiment === "negative" &&
                                        "bg-red-50 text-red-600 border-red-200",
                                      response.sentiment === "neutral" &&
                                        "bg-gray-50 text-gray-500 border-gray-200",
                                    )}
                                  >
                                    {response.sentiment === "positive"
                                      ? `😊 ${t("Positivo")}`
                                      : response.sentiment === "negative"
                                        ? `😟 ${t("Negativo")}`
                                        : `😐 ${t("Neutro")}`}
                                  </span>
                                )}
                                {response.message_type && (
                                  <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full border bg-purple-50 text-purple-600 border-purple-200">
                                    {MESSAGE_TYPE_PT[response.message_type] ??
                                      response.message_type}
                                  </span>
                                )}
                                {(response.custom_tags || []).map((tagId) => {
                                  const tag = customTags.find(
                                    (ct) => ct.id === tagId,
                                  );
                                  if (!tag) return null;
                                  return (
                                    <span
                                      key={tag.id}
                                      className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full border"
                                      style={{
                                        background: `${tag.color}20`,
                                        borderColor: `${tag.color}40`,
                                        color: tag.color,
                                      }}
                                    >
                                      {tag.name}
                                    </span>
                                  );
                                })}
                                <div className="flex items-center gap-1 text-[11px] font-black text-purple-600">
                                  <span>
                                    {((response.confidence || 0) * 100).toFixed(
                                      0,
                                    )}
                                    %
                                  </span>
                                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                                    {t("Confiança IA")}
                                  </span>
                                </div>
                                <div
                                  className={cn(
                                    "py-1 px-2.5 rounded-lg border font-black uppercase tracking-widest text-[9px] flex items-center gap-1.5",
                                    status.variant === "emerald"
                                      ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                      : status.variant === "amber"
                                        ? "bg-amber-50 text-amber-600 border-amber-100"
                                        : status.variant === "red"
                                          ? "bg-red-50 text-red-600 border-red-100"
                                          : status.variant === "blue"
                                            ? "bg-blue-50 text-blue-600 border-blue-100"
                                            : status.variant === "purple"
                                              ? "bg-purple-50 text-purple-600 border-purple-200"
                                              : "bg-white text-gray-600 border-purple-200",
                                  )}
                                >
                                  <StatusIcon size={12} strokeWidth={3} />
                                  {status.label}
                                </div>

                                <span className="text-[10px] font-mono font-medium text-gray-400 bg-gray-50 border border-gray-100 rounded-sm px-1.5 py-0.5 shrink-0">
                                  #{response.id}
                                </span>
                                <ActionDropdown
                                  icon={MoreVertical}
                                  size="sm"
                                  align="right"
                                  variant="compact"
                                  tooltip={t("Mais opções")}
                                  loading={rowActionLoading === response.id}
                                  buttonClassName="h-8! w-8! rounded-lg border-gray-200 bg-white hover:bg-gray-50 text-gray-500 hover:text-purple-600 shadow-xs"
                                  actions={[
                                    ...(response.status === "pending"
                                      ? [
                                          {
                                            label: t("Aprovar"),
                                            icon: Check,
                                            variant: "success" as const,
                                            loading:
                                              rowActionLoading === response.id,
                                            onClick: async () => {
                                              setRowActionLoading(response.id);
                                              try {
                                                await handleApproveResponse(
                                                  response.id,
                                                );
                                              } finally {
                                                setRowActionLoading(null);
                                              }
                                            },
                                          },
                                          {
                                            label: t("Rejeitar"),
                                            icon: X,
                                            variant: "danger" as const,
                                            loading:
                                              rowActionLoading === response.id,
                                            onClick: async () => {
                                              setRowActionLoading(response.id);
                                              try {
                                                await handleRejectResponse(
                                                  response.id,
                                                  "Inadequado",
                                                );
                                              } finally {
                                                setRowActionLoading(null);
                                              }
                                            },
                                          },
                                        ]
                                      : []),
                                    ...(response.status === "approved"
                                      ? [
                                          {
                                            label: t("Enviar"),
                                            icon: Check,
                                            variant: "success" as const,
                                            loading:
                                              rowActionLoading === response.id,
                                            onClick: async () => {
                                              setRowActionLoading(response.id);
                                              try {
                                                await handleSendResponse(
                                                  response.id,
                                                  response,
                                                );
                                              } finally {
                                                setRowActionLoading(null);
                                              }
                                            },
                                          },
                                        ]
                                      : []),
                                    {
                                      label: t("Editar"),
                                      icon: Edit3,
                                      onClick: () =>
                                        openResponseModal(response),
                                    },
                                  ]}
                                />
                              </div>
                            </div>

                            {response.source_detail && (
                              <p className="text-[10px] text-gray-400 font-semibold -mt-1">
                                {response.source_detail}
                              </p>
                            )}

                            {/* Comentário e Resposta lado a lado */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                              <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2 opacity-50 group-hover:opacity-100 transition-opacity">
                                  <MessageSquare
                                    size={14}
                                    className="text-blue-500"
                                  />
                                  <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-widest">
                                    {t("Comentário Recebido")}
                                  </h4>
                                </div>
                                <p className="flex-1 text-[13px] text-gray-700 leading-relaxed font-medium bg-gray-50/50 p-3 rounded-xl border border-purple-200 italic group-hover:bg-white transition-all">
                                  "{response.trigger}"
                                </p>
                              </div>

                              <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2 opacity-50 group-hover:opacity-100 transition-opacity">
                                  <Bot size={14} className="text-purple-500" />
                                  <h4 className="text-[10px] font-black text-purple-600 uppercase tracking-widest">
                                    {t("Resposta Sugerida")}
                                  </h4>
                                </div>
                                <p className="flex-1 text-[14px] text-gray-800 leading-relaxed font-semibold bg-purple-50/20 p-3 rounded-xl border border-purple-200/50 group-hover:bg-purple-50/40 transition-all shadow-xs">
                                  {response.response}
                                </p>
                              </div>
                            </div>

                            {(response.organization_id ||
                              response.client_company_id ||
                              response.lead_id) && (
                              <div className="pt-3 border-t border-purple-50">
                                <div className="flex items-center gap-3 flex-wrap">
                                  {response.organization_id && (
                                    <span className="text-[9px] text-gray-300 font-mono">
                                      org:{response.organization_id}
                                    </span>
                                  )}
                                  {response.client_company_id && (
                                    <span className="text-[9px] text-gray-300 font-mono">
                                      cc:{response.client_company_id}
                                    </span>
                                  )}
                                  {response.lead_id && (
                                    <span
                                      className="text-[9px] text-gray-300 font-mono truncate max-w-[200px]"
                                      title={response.lead_id}
                                    >
                                      lead:{response.lead_id}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </Card>
                      );
                    })
                  ) : (
                    <div className="bg-white/50 border border-purple-200/50 rounded-3xl py-12 sm:py-16 shadow-xs flex flex-col items-center justify-center">
                      <EmptyState
                        title={t("Nenhuma interação encontrada")}
                        description={t(
                          "Não existem respostas geradas para os filtros selecionados.",
                        )}
                        icon={
                          <Search className="h-16 w-16 sm:h-20 sm:w-20 text-gray-400" />
                        }
                        action={
                          (filters.query ||
                            filters.status !== "all" ||
                            filters.platform !== "all" ||
                            filters.sentiment !== "all" ||
                            filters.message_type !== "all" ||
                            filters.source_channel !== "all" ||
                            filters.tag !== "all") && (
                            <Button
                              variant="primary"
                              icon={<X className="h-4 w-4" />}
                              onClick={() =>
                                setFilters({
                                  ...filters,
                                  query: "",
                                  status: "all",
                                  platform: "all",
                                  sentiment: "all",
                                  message_type: "all",
                                  source_channel: "all",
                                  tag: "all",
                                })
                              }
                            >
                              {t("Limpar filtros")}
                            </Button>
                          )
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === "conhecimento" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap h-[52px] bg-white/70 backdrop-blur-xs border border-purple-200 rounded-2xl px-4 sm:px-5 py-2.5 shadow-xs">
                <div className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">
                    {t("Base de Conhecimento")}
                  </h2>
                </div>
                <span className="text-xs text-gray-400 font-medium">
                  {t("{count} templates · {urls} URLs indexadas", {
                    count: templates.length,
                    urls: knowledgeUrlsText.split("\n").filter((u) => u.trim())
                      .length,
                  })}
                </span>
              </div>

              {templatesError && (
                <Alert
                  variant="error"
                  title={t("Não foi possível carregar os templates")}
                  dismissible
                  onDismiss={() => setTemplatesError(null)}
                  className="bg-red-50 border-red-100"
                >
                  {templatesError}
                </Alert>
              )}

              <div className="bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-3 sm:p-4">
                <SectionHeader
                  icon={<Globe className="w-5 h-5" />}
                  title={t("Fontes Externas (RAG)")}
                  subtitle={t(
                    "URLs indexadas para embasar as respostas do agente",
                  )}
                  action={
                    <Button
                      size="sm"
                      isLoading={knowledgeUrlsSaving}
                      disabled={knowledgeUrlsSaving}
                      icon={<Check className="h-3.5 w-3.5" />}
                      onClick={handleSaveKnowledgeUrls}
                      className="rounded-lg h-8! min-h-[32px]! py-1! border! border-blue-300! text-blue-700! bg-blue-50! hover:bg-blue-100! hover:text-blue-700! shadow-xs! hover:shadow-xs! shrink-0"
                    >
                      <span className="text-xs">{t("Salvar Fontes")}</span>
                    </Button>
                  }
                />
                <Textarea
                  value={knowledgeUrlsText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setKnowledgeUrlsText(e.target.value)
                  }
                  placeholder={
                    "https://meusite.com.br/faq\nhttps://meusite.com.br/catalogo.pdf (uma URL por linha)"
                  }
                  rows={2}
                  className="font-mono text-xs"
                />
              </div>

              <div className="bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-3 sm:p-4">
                <SectionHeader
                  icon={<Tag className="w-5 h-5" />}
                  title={t("Templates de Resposta")}
                  subtitle={t(
                    "Respostas prontas para reutilizar por plataforma",
                  )}
                  action={
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Plus className="h-3.5 w-3.5" />}
                      className="rounded-lg h-8! min-h-[32px]! py-1! border! border-purple-300! text-purple-700! bg-purple-50! hover:bg-purple-100! hover:text-purple-700! shadow-xs! hover:shadow-xs! shrink-0"
                      onClick={() => openTemplateModal(null)}
                    >
                      <span className="text-xs">{t("Novo Template")}</span>
                    </Button>
                  }
                />
                <ResponsiveGrid cols={{ default: 1, sm: 2, lg: 3 }} gap="sm">
                  {templates.length > 0 ? (
                    templates.map((template) => (
                      <Card
                        key={template.id}
                        className="p-0! overflow-hidden border-purple-200! hover:border-purple-300! hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 flex flex-col h-full bg-white group rounded-3xl"
                      >
                        <div className="p-3 pb-2">
                          <div className="flex justify-between items-start mb-2">
                            <div
                              className={cn(
                                "w-8 h-8 rounded-xl flex items-center justify-center border transition-all duration-300",
                                template.platform === "all"
                                  ? "bg-purple-50 text-purple-600 border-purple-200"
                                  : getPlatformChipClasses(template.platform)
                                      .base,
                              )}
                            >
                              {template.platform === "all" ? (
                                <Globe size={16} />
                              ) : (
                                <PlatformIcon
                                  platform={template.platform as any}
                                  size={16}
                                />
                              )}
                            </div>
                            <Badge
                              variant={
                                template.is_active ? "success" : "secondary"
                              }
                              className="py-1 px-3 text-[9px] font-black uppercase tracking-widest rounded-full"
                            >
                              {template.is_active ? t("Ativo") : t("Pausado")}
                            </Badge>
                          </div>
                          <h4 className="font-black text-[12px] text-purple-900 uppercase tracking-[0.15em] mb-1.5 truncate group-hover:text-purple-600 transition-colors">
                            {template.name}
                          </h4>
                          <div className="bg-gray-50/50 p-2.5 rounded-xl border border-purple-100 group-hover:border-purple-200 transition-all">
                            <p className="text-[13px] text-gray-600 leading-snug line-clamp-2 font-medium italic">
                              "{template.content}"
                            </p>
                          </div>
                        </div>

                        <div className="mt-auto p-3 pt-2 border-t border-purple-50 flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-lg h-8! w-8! min-h-[32px]! p-0! border! border-purple-300! text-purple-700! bg-purple-50! hover:bg-purple-100! hover:text-purple-700! shadow-xs! hover:shadow-xs!"
                              onClick={() => openTemplateModal(template)}
                            >
                              <Edit3 size={14} />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              isLoading={templateDeletingId === template.id}
                              disabled={templateDeletingId !== null}
                              className="rounded-lg h-8! w-8! min-h-[32px]! p-0! border! border-red-300! text-red-700! bg-red-50! hover:bg-red-100! hover:text-red-700! shadow-xs! hover:shadow-xs!"
                              onClick={() => handleDeleteTemplate(template.id)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-[9px] font-black text-purple-300 uppercase tracking-widest mb-0.5">
                              {t("Utilização")}
                            </span>
                            <span className="text-[11px] font-black text-purple-600 uppercase tracking-widest bg-purple-50 px-2 py-0.5 rounded-lg border border-purple-200">
                              {template.usage_count}x
                            </span>
                          </div>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="col-span-full bg-white/50 border border-purple-200/50 rounded-3xl py-12 sm:py-16 shadow-xs flex flex-col items-center justify-center">
                      <EmptyState
                        title={t("Nenhum template criado")}
                        description={t(
                          "Crie seu primeiro template de resposta para agilizar seu atendimento.",
                        )}
                        icon={
                          <Plus className="h-16 w-16 sm:h-20 sm:w-20 text-gray-400" />
                        }
                        action={
                          <Button
                            variant="primary"
                            icon={<Plus className="h-4 w-4" />}
                            onClick={() => openTemplateModal(null)}
                          >
                            {t("Novo Template")}
                          </Button>
                        }
                      />
                      <div className="w-full max-w-2xl mt-8 px-4">
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest text-center mb-3">
                          {t("Ou comece com uma sugestão")}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {TEMPLATE_SUGGESTIONS.map((suggestion) => (
                            <button
                              key={suggestion.name}
                              onClick={() => openTemplateSuggestion(suggestion)}
                              className="text-left p-3 rounded-xl border border-purple-200 bg-white hover:border-purple-300 hover:shadow-md transition-all"
                            >
                              <p className="text-[11px] font-black text-purple-900 uppercase tracking-wide mb-1">
                                {suggestion.name}
                              </p>
                              <p className="text-[11px] text-gray-500 line-clamp-2 leading-snug">
                                {suggestion.content}
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </ResponsiveGrid>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "configuracao" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap h-[52px] bg-white/70 backdrop-blur-xs border border-purple-200 rounded-2xl px-4 sm:px-5 py-2.5 shadow-xs">
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">
                    {t("Configuração Global do Agente")}
                  </h2>
                </div>
                <UITooltip
                  content={
                    isAgentOperational
                      ? t(
                          "O agente global está ativado e respondendo automaticamente",
                        )
                      : t(
                          "O agente global está desativado — ative abaixo para começar a responder automaticamente",
                        )
                  }
                  position="left"
                >
                  <div
                    className={cn(
                      "flex items-center gap-1.5 px-2.5 py-1 backdrop-blur-xs border rounded-full shadow-xs",
                      isAgentOperational
                        ? "bg-emerald-50/80 border-emerald-100/50"
                        : "bg-gray-50 border-gray-200",
                    )}
                  >
                    <div className="relative flex h-1.5 w-1.5">
                      {isAgentOperational && (
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      )}
                      <span
                        className={cn(
                          "relative inline-flex rounded-full h-1.5 w-1.5",
                          isAgentOperational ? "bg-emerald-500" : "bg-gray-400",
                        )}
                      ></span>
                    </div>
                    <span
                      className={cn(
                        "text-[9px] font-black uppercase tracking-[0.2em] leading-none",
                        isAgentOperational
                          ? "text-emerald-700"
                          : "text-gray-500",
                      )}
                    >
                      {isAgentOperational
                        ? t("IA Operacional")
                        : t("IA Inativa")}
                    </span>
                  </div>
                </UITooltip>
              </div>

              <div className="bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-3 sm:p-4">
                <AgentConfigPanel
                  platform="global"
                  status={platformStatus?.global}
                  updatePlatform={async (_, updates) =>
                    await updateGlobalAgent(updates)
                  }
                />
              </div>

              <div className="bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-3 sm:p-4">
                <SectionHeader
                  icon={<Settings className="w-5 h-5" />}
                  title={t("Status por Plataforma")}
                  subtitle={t(
                    "Ative o auto-responder e ajuste a confiança mínima por rede social",
                  )}
                />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                  {(
                    [
                      "instagram",
                      "facebook",
                      "linkedin",
                      "google_my_business",
                      "messenger",
                    ] as const
                  ).map((platform) => {
                    const status = platformStatus?.[platform];
                    return (
                      <div
                        key={platform}
                        className="p-3 rounded-2xl border border-purple-200 hover:bg-white/90 hover:border-purple-300 transition-all duration-300 group"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "w-10 h-10 rounded-xl border flex items-center justify-center transition-all duration-300 shadow-xs shrink-0",
                              getPlatformConfig(platform).bgColor,
                              getPlatformConfig(platform).color,
                              getPlatformConfig(platform).borderColor,
                            )}
                          >
                            <PlatformIcon platform={platform} size={20} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-black text-xs text-gray-900 uppercase tracking-widest truncate">
                                {getPlatformName(platform)}
                              </h4>
                              <div
                                className={cn(
                                  "h-2 w-2 rounded-full shrink-0 animate-pulse",
                                  status?.enabled
                                    ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                                    : "bg-gray-300",
                                )}
                              />
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest opacity-70 truncate">
                                {t("Respostas automáticas com IA")}
                              </p>
                              <div
                                className={cn(
                                  "inline-flex items-center px-2 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-widest transition-all shrink-0",
                                  status?.using_rag
                                    ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                    : "bg-gray-50 text-gray-400 border-purple-200",
                                )}
                              >
                                <Zap
                                  size={9}
                                  className={cn(
                                    "mr-1",
                                    status?.using_rag ? "fill-emerald-600" : "",
                                  )}
                                />
                                {status?.using_rag
                                  ? t("Conectado (RAG)")
                                  : t("Modo Manual")}
                              </div>
                            </div>
                          </div>
                          <UITooltip
                            content={
                              status?.enabled
                                ? t(
                                    "Para de processar comentários/mensagens desta rede — a IA deixa de gerar respostas até você reativar.",
                                  )
                                : t(
                                    "Volta a processar comentários/mensagens desta rede automaticamente.",
                                  )
                            }
                            position="top"
                          >
                            <Button
                              size="sm"
                              isLoading={
                                platformActionLoading === `${platform}:enabled`
                              }
                              disabled={platformActionLoading !== null}
                              className={cn(
                                "rounded-lg h-9! w-auto! min-h-[36px]! py-1! border! shadow-xs! hover:shadow-xs! shrink-0",
                                status?.enabled
                                  ? "border-purple-300! text-purple-700! bg-purple-50! hover:bg-purple-100! hover:text-purple-700!"
                                  : "border-emerald-300! text-emerald-700! bg-emerald-50! hover:bg-emerald-100! hover:text-emerald-700!",
                              )}
                              onClick={async () => {
                                setPlatformActionLoading(`${platform}:enabled`);
                                try {
                                  await updatePlatform(platform, {
                                    enabled: !status?.enabled,
                                  });
                                } finally {
                                  setPlatformActionLoading(null);
                                }
                              }}
                            >
                              <span className="text-xs">
                                {status?.enabled ? t("Desativar") : t("Ativar")}
                              </span>
                            </Button>
                          </UITooltip>
                        </div>

                        {/* Advanced Settings for this platform (only visible if enabled) */}
                        {status?.enabled && (
                          <div className="pt-3 mt-3 border-t border-purple-50 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5 animate-in fade-in slide-in-from-top-4">
                            <div className="flex flex-col gap-2 md:pr-5 md:border-r md:border-purple-50">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-700 uppercase tracking-widest flex items-center gap-2">
                                  <Zap className="h-3 w-3 text-emerald-500" />
                                  {t("Piloto Automático (Auto-Aprovação)")}
                                </span>
                                <Button
                                  size="sm"
                                  isLoading={
                                    platformActionLoading ===
                                    `${platform}:auto_approve`
                                  }
                                  disabled={platformActionLoading !== null}
                                  className={cn(
                                    "rounded-lg h-7! min-h-[28px]! py-0.5! px-3! border! shadow-xs! hover:shadow-xs!",
                                    status?.auto_approve
                                      ? "border-emerald-300! text-emerald-700! bg-emerald-50! hover:bg-emerald-100! hover:text-emerald-700!"
                                      : "border-gray-300! text-gray-500! bg-white! hover:bg-gray-50! hover:text-gray-600!",
                                  )}
                                  onClick={async () => {
                                    setPlatformActionLoading(
                                      `${platform}:auto_approve`,
                                    );
                                    try {
                                      await updatePlatform(platform, {
                                        auto_approve: !status?.auto_approve,
                                      });
                                    } finally {
                                      setPlatformActionLoading(null);
                                    }
                                  }}
                                >
                                  <span className="text-[10px] font-black">
                                    {status?.auto_approve
                                      ? t("Ligado")
                                      : t("Desligado")}
                                  </span>
                                </Button>
                              </div>
                              <p className="text-[10px] text-gray-400 font-medium leading-snug">
                                {t(
                                  "Se ligado, a IA enviará as respostas automaticamente para a rede social sem passar pela sua aprovação manual, desde que a confiança seja maior que o mínimo configurado abaixo.",
                                )}
                              </p>
                            </div>

                            <div className="flex flex-col gap-2">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-700 uppercase tracking-widest flex items-center gap-2">
                                  <Activity className="h-3 w-3 text-purple-500" />
                                  {t("Confiança Mínima")}
                                </span>
                                <span className="inline-flex items-center justify-center rounded-lg h-7 px-3 border border-purple-300 bg-purple-50 text-purple-700 text-[10px] font-black tabular-nums">
                                  {Math.round(
                                    (status?.min_confidence ?? 0.8) * 100,
                                  )}
                                  %
                                </span>
                              </div>
                              <div className="flex items-center gap-4">
                                <input
                                  type="range"
                                  min="0.5"
                                  max="0.99"
                                  step="0.01"
                                  value={status?.min_confidence ?? 0.8}
                                  onChange={(e) =>
                                    updatePlatform(platform, {
                                      min_confidence: parseFloat(
                                        e.target.value,
                                      ),
                                    })
                                  }
                                  className="w-full accent-purple-600"
                                />
                              </div>
                              <p className="text-[10px] text-gray-400 font-medium leading-snug">
                                {t(
                                  "Respostas com confiança abaixo deste valor ({percent}%) sempre cairão na fila de moderação humana (Pendente), mesmo com o piloto automático ligado.",
                                  {
                                    percent: (
                                      (status?.min_confidence ?? 0.8) * 100
                                    ).toFixed(0),
                                  },
                                )}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tag Manager */}
              <div className="bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-3 sm:p-4">
                <SectionHeader
                  icon={<Tag className="w-5 h-5" />}
                  title={t("Gerenciador de Tags")}
                  subtitle={t(
                    "Etiquetas personalizadas para organizar interações",
                  )}
                />
                <div className="space-y-3">
                  {/* Create tag */}
                  <div className="flex items-center gap-1 h-9 rounded-xl border border-purple-200 bg-gray-50 pl-1 pr-1 transition-colors focus-within:border-purple-300 focus-within:bg-white">
                    <UITooltip content={t("Cor da tag")} position="top">
                      <input
                        type="color"
                        value={newTagColor}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        className="h-7 w-7 rounded-lg border border-purple-200 cursor-pointer shrink-0 bg-transparent p-0.5"
                      />
                    </UITooltip>
                    <input
                      type="text"
                      placeholder={t("Nome da tag (ex: VIP, Urgente)")}
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleCreateTag()}
                      maxLength={TAG_NAME_MAX_LENGTH}
                      className="flex-1 min-w-0 bg-transparent text-sm font-medium text-gray-900 outline-hidden placeholder:text-gray-400 px-1.5"
                    />
                    <span
                      className={cn(
                        "shrink-0 px-1 text-[10px] font-bold tabular-nums",
                        newTagName.length >= TAG_NAME_MAX_LENGTH
                          ? "text-red-400"
                          : "text-gray-300",
                      )}
                    >
                      {newTagName.length}/{TAG_NAME_MAX_LENGTH}
                    </span>
                    <button
                      type="button"
                      onClick={handleCreateTag}
                      disabled={tagLoading || !newTagName.trim()}
                      className="shrink-0 h-7 w-7 rounded-lg flex items-center justify-center transition-colors text-purple-600 hover:bg-purple-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Preview */}
                  {newTagName.trim() && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-400 font-medium">
                        {t("Preview:")}
                      </span>
                      <span
                        className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border"
                        style={{
                          background: `${newTagColor}20`,
                          borderColor: `${newTagColor}40`,
                          color: newTagColor,
                        }}
                      >
                        {newTagName}
                      </span>
                    </div>
                  )}
                  {/* Tag list */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {customTags.length === 0 ? (
                      <p className="text-[11px] text-gray-400">
                        {t("Nenhuma tag criada ainda.")}
                      </p>
                    ) : (
                      customTags.map((tag) => (
                        <div key={tag.id} className="flex items-center gap-1.5">
                          <span
                            className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border"
                            style={{
                              background: `${tag.color}20`,
                              borderColor: `${tag.color}40`,
                              color: tag.color,
                            }}
                          >
                            {tag.name}
                          </span>
                          <UITooltip content={t("Remover tag")} position="top">
                            <button
                              onClick={() => handleDeleteTag(tag.id)}
                              disabled={tagDeletingId !== null}
                              className={cn(
                                "text-gray-300 hover:text-red-400 transition-colors disabled:cursor-not-allowed",
                                tagDeletingId === tag.id &&
                                  "animate-pulse text-red-400",
                              )}
                            >
                              <X size={12} />
                            </button>
                          </UITooltip>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Response Details Modal */}
          {showResponseModal && selectedResponse && (
            <Modal
              open={showResponseModal}
              onClose={() => setShowResponseModal(false)}
              closeOnOverlayClick
              title={t("Detalhes da Interação")}
              subtitle={`${getPlatformName(selectedResponse.platform)} • ${
                selectedResponse.created_at
                  ? new Date(selectedResponse.created_at).toLocaleString(
                      "pt-BR",
                      {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )
                  : ""
              }`}
              icon={<MessageSquare className="h-5 w-5" />}
              maxWidth="max-w-2xl"
              footer={
                <div className="flex flex-wrap items-center justify-end gap-2 w-full">
                  <Button
                    size="sm"
                    disabled={modalActionLoading !== null}
                    onClick={() => setShowResponseModal(false)}
                    className="rounded-lg h-9! w-auto! border! border-gray-300! text-gray-600! bg-white! hover:bg-gray-50! hover:text-gray-700! shadow-xs! hover:shadow-xs!"
                  >
                    <span className="text-xs">{t("Fechar")}</span>
                  </Button>
                  {selectedResponse.status === "pending" && (
                    <>
                      <Button
                        size="sm"
                        isLoading={modalActionLoading === "update"}
                        disabled={modalActionLoading !== null}
                        onClick={async () => {
                          setModalActionLoading("update");
                          try {
                            await handleUpdateResponse(selectedResponse.id);
                          } finally {
                            setModalActionLoading(null);
                          }
                        }}
                        className="rounded-lg h-9! w-auto! border! border-purple-300! text-purple-700! bg-purple-50! hover:bg-purple-100! hover:text-purple-700! shadow-xs! hover:shadow-xs!"
                      >
                        <span className="text-xs">{t("Salvar Alteração")}</span>
                      </Button>
                      <Button
                        size="sm"
                        isLoading={modalActionLoading === "approve"}
                        disabled={modalActionLoading !== null}
                        onClick={async () => {
                          setModalActionLoading("approve");
                          try {
                            await handleUpdateResponse(selectedResponse.id);
                            await handleApproveResponse(selectedResponse.id);
                            setShowResponseModal(false);
                          } finally {
                            setModalActionLoading(null);
                          }
                        }}
                        className="rounded-lg h-9! w-auto! border! border-emerald-300! text-emerald-700! bg-emerald-50! hover:bg-emerald-100! hover:text-emerald-700! shadow-xs! hover:shadow-xs!"
                      >
                        <span className="text-xs">{t("Apenas Aprovar")}</span>
                      </Button>
                      <Button
                        size="sm"
                        isLoading={modalActionLoading === "send"}
                        disabled={modalActionLoading !== null}
                        onClick={async () => {
                          setModalActionLoading("send");
                          try {
                            await handleUpdateResponse(selectedResponse.id);
                            // O backend agora aprova automaticamente ao chamar /send
                            await handleSendResponse(
                              selectedResponse.id,
                              selectedResponse,
                            );
                            setShowResponseModal(false);
                          } finally {
                            setModalActionLoading(null);
                          }
                        }}
                        className="rounded-lg h-9! w-auto! border! border-blue-300! text-blue-700! bg-blue-50! hover:bg-blue-100! hover:text-blue-700! shadow-xs! hover:shadow-xs!"
                      >
                        <span className="text-xs">{t("Aprovar e Enviar")}</span>
                      </Button>
                    </>
                  )}
                  {selectedResponse.status === "approved" && (
                    <Button
                      size="sm"
                      isLoading={modalActionLoading === "send"}
                      disabled={modalActionLoading !== null}
                      onClick={async () => {
                        setModalActionLoading("send");
                        try {
                          await handleSendResponse(selectedResponse.id);
                          setShowResponseModal(false);
                        } finally {
                          setModalActionLoading(null);
                        }
                      }}
                      className="rounded-lg h-9! w-auto! border! border-blue-300! text-blue-700! bg-blue-50! hover:bg-blue-100! hover:text-blue-700! shadow-xs! hover:shadow-xs!"
                    >
                      <span className="text-xs">{t("Enviar Resposta")}</span>
                    </Button>
                  )}
                </div>
              }
            >
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-1">
                    <MessageSquare size={12} className="text-blue-500" />
                    <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-widest">
                      {selectedResponse.metadata?.conversation_history?.length
                        ? t("Histórico da Conversa")
                        : t("Gatilho do Usuário")}
                    </h4>
                  </div>
                  {selectedResponse.metadata?.conversation_history?.length ? (
                    <Card className="p-4 bg-gray-50/50 border-gray-200! flex flex-col gap-4 max-h-[300px] overflow-y-auto">
                      {selectedResponse.metadata.conversation_history.map(
                        (msg: any, idx: number) => {
                          const isLead = msg.is_user;
                          return (
                            <div
                              key={idx}
                              className={cn(
                                "flex flex-col max-w-[85%]",
                                !isLead
                                  ? "self-end items-end"
                                  : "self-start items-start",
                              )}
                            >
                              <span className="text-[9px] text-gray-400 font-medium mb-1 px-1">
                                {!isLead
                                  ? t("Nossa Equipe")
                                  : msg.sender || t("Usuário")}{" "}
                                •{" "}
                                {new Date(msg.created_at).toLocaleTimeString(
                                  "pt-BR",
                                  { hour: "2-digit", minute: "2-digit" },
                                )}
                              </span>
                              <div
                                className={cn(
                                  "p-3 rounded-2xl text-xs font-medium leading-relaxed shadow-xs",
                                  !isLead
                                    ? "bg-purple-100 text-purple-900 rounded-tr-sm"
                                    : "bg-white border border-gray-200 text-gray-800 rounded-tl-sm",
                                )}
                              >
                                {msg.text}
                              </div>
                            </div>
                          );
                        },
                      )}
                    </Card>
                  ) : (
                    <Card className="p-4 bg-blue-50/30 border-blue-200!">
                      <p className="text-xs font-medium text-gray-800 italic leading-relaxed">
                        "{selectedResponse.trigger}"
                      </p>
                    </Card>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <Bot size={12} className="text-purple-500" />
                      <h4 className="text-[10px] font-black text-purple-600 uppercase tracking-widest">
                        {t("Resposta Gerada pela IA")}
                      </h4>
                    </div>
                    {selectedResponse.status === "pending" && (
                      <span className="text-[9px] font-bold text-amber-500 uppercase tracking-widest">
                        {t("Modo Edição")}
                      </span>
                    )}
                  </div>
                  <Card className="p-0 overflow-hidden bg-purple-50/10 border-purple-200!">
                    {selectedResponse.status === "pending" ? (
                      <textarea
                        className="w-full p-4 text-xs font-medium text-gray-800 leading-relaxed bg-transparent border-none focus:ring-0 resize-none min-h-[120px]"
                        value={editingResponseText}
                        onChange={(e) => setEditingResponseText(e.target.value)}
                      />
                    ) : (
                      <div className="p-4">
                        <p className="text-xs font-medium text-gray-800 leading-relaxed">
                          {selectedResponse.response}
                        </p>
                      </div>
                    )}
                  </Card>

                  {/* AI Suggestions */}
                  {selectedResponse.status === "pending" && (
                    <div className="mt-3 space-y-2">
                      <Button
                        size="sm"
                        onClick={handleFetchSuggestions}
                        disabled={loadingSuggestions}
                        icon={
                          loadingSuggestions ? (
                            <span className="inline-block w-3 h-3 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <span>✨</span>
                          )
                        }
                        className="rounded-lg h-8! min-h-[32px]! py-1! border! border-violet-300! text-violet-700! bg-violet-50! hover:bg-violet-100! hover:text-violet-700! shadow-xs! hover:shadow-xs! disabled:opacity-50!"
                      >
                        <span className="text-xs">
                          {loadingSuggestions
                            ? t("Gerando alternativas...")
                            : t("Sugerir 3 alternativas")}
                        </span>
                      </Button>
                      {suggestions.length > 0 && (
                        <div className="flex flex-col gap-2">
                          {suggestions.map((s, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setEditingResponseText(s)}
                              className="w-full text-left text-xs px-3 py-2 rounded-xl border border-violet-200 bg-violet-50/60 text-violet-900 hover:bg-violet-100 hover:border-violet-400 transition-all font-medium leading-relaxed"
                            >
                              <span className="text-[9px] font-black text-violet-400 uppercase tracking-widest mr-1.5">
                                {i + 1}.
                              </span>
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="p-3 bg-gray-50/50 rounded-xl border border-purple-200 text-center">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
                      {t("Nível de Confiança")}
                    </p>
                    <p className="text-xl font-black text-purple-600">
                      {(selectedResponse.confidence * 100).toFixed(0)}%
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50/50 rounded-xl border border-purple-200 text-center">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
                      {t("Plataforma Origem")}
                    </p>
                    <div className="flex items-center justify-center gap-2 pt-0.5">
                      <span
                        className={
                          getPlatformConfig(selectedResponse.platform).color
                        }
                      >
                        <PlatformIcon
                          platform={selectedResponse.platform}
                          size={16}
                        />
                      </span>
                      <p className="text-xs font-black text-gray-900 uppercase tracking-tight capitalize">
                        {getPlatformName(selectedResponse.platform)}
                      </p>
                    </div>
                    {selectedResponse.source_detail && (
                      <p className="text-[9px] text-gray-400 mt-1 truncate">
                        {selectedResponse.source_detail}
                      </p>
                    )}
                  </div>
                  {selectedResponse.sentiment && (
                    <div className="p-3 bg-gray-50/50 rounded-xl border border-purple-200 text-center">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
                        {t("Sentimento")}
                      </p>
                      <span
                        className={cn(
                          "text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border",
                          selectedResponse.sentiment === "positive" &&
                            "bg-emerald-50 text-emerald-600 border-emerald-200",
                          selectedResponse.sentiment === "negative" &&
                            "bg-red-50 text-red-600 border-red-200",
                          selectedResponse.sentiment === "neutral" &&
                            "bg-gray-50 text-gray-500 border-gray-200",
                        )}
                      >
                        {selectedResponse.sentiment === "positive"
                          ? `😊 ${t("Positivo")}`
                          : selectedResponse.sentiment === "negative"
                            ? `😟 ${t("Negativo")}`
                            : `😐 ${t("Neutro")}`}
                      </span>
                    </div>
                  )}
                  {selectedResponse.message_type && (
                    <div className="p-3 bg-gray-50/50 rounded-xl border border-purple-200 text-center">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
                        {t("Tipo de Mensagem")}
                      </p>
                      <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border bg-purple-50 text-purple-600 border-purple-200">
                        {MESSAGE_TYPE_PT[selectedResponse.message_type] ??
                          selectedResponse.message_type}
                      </span>
                    </div>
                  )}
                  {selectedResponse.metadata?.post_id && (
                    <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-200 text-center">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
                        {t("Post de Origem")}
                      </p>
                      <button
                        onClick={() => fetchOriginPost(selectedResponse.id)}
                        className="text-[11px] font-bold text-blue-600 hover:text-blue-800 underline"
                      >
                        {t("Ver post de origem")}
                      </button>
                    </div>
                  )}
                </div>

                {/* Edição de classificação */}
                <div className="border border-purple-100 rounded-2xl p-3 bg-purple-50/30 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Tag size={12} className="text-purple-500" />
                    <span className="text-[10px] font-black text-purple-600 uppercase tracking-widest">
                      {t("Classificação (editável)")}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">
                        {t("Sentimento")}
                      </label>
                      <Select
                        size="sm"
                        value={editingSentiment}
                        onChange={setEditingSentiment}
                        placeholder={t("— auto —")}
                        options={[
                          {
                            value: "",
                            label: t("— auto —"),
                            icon: <ListFilter />,
                          },
                          {
                            value: "positive",
                            label: t("Positivo"),
                            icon: <Smile />,
                          },
                          {
                            value: "neutral",
                            label: t("Neutro"),
                            icon: <Meh />,
                          },
                          {
                            value: "negative",
                            label: t("Negativo"),
                            icon: <Frown />,
                          },
                        ]}
                      />
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">
                        {t("Tipo")}
                      </label>
                      <Select
                        size="sm"
                        value={editingMessageType}
                        onChange={setEditingMessageType}
                        placeholder={t("— auto —")}
                        options={[
                          {
                            value: "",
                            label: t("— auto —"),
                            icon: <ListFilter />,
                          },
                          {
                            value: "question",
                            label: t("Pergunta"),
                            icon: <HelpCircle />,
                          },
                          {
                            value: "complaint",
                            label: t("Reclamação"),
                            icon: <AlertTriangle />,
                          },
                          {
                            value: "compliment",
                            label: t("Elogio"),
                            icon: <ThumbsUp />,
                          },
                          {
                            value: "lead",
                            label: "Lead",
                            icon: <UserPlus />,
                          },
                          {
                            value: "spam",
                            label: "Spam",
                            icon: <ShieldAlert />,
                          },
                          {
                            value: "other",
                            label: t("Outro"),
                            icon: <MoreHorizontal />,
                          },
                        ]}
                      />
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">
                        {t("Canal")}
                      </label>
                      <Select
                        size="sm"
                        value={editingChannel}
                        onChange={setEditingChannel}
                        placeholder={t("— auto —")}
                        options={[
                          {
                            value: "",
                            label: t("— auto —"),
                            icon: <ListFilter />,
                          },
                          {
                            value: "comment",
                            label: t("Comentário"),
                            icon: <MessageSquare />,
                          },
                          {
                            value: "direct",
                            label: "Direct",
                            icon: <Mail />,
                          },
                          {
                            value: "messenger",
                            label: "Messenger",
                            icon: <MessageCircle />,
                          },
                          {
                            value: "story_reply",
                            label: "Story Reply",
                            icon: <Camera />,
                          },
                        ]}
                      />
                    </div>
                  </div>
                  {/* Custom tags */}
                  {customTags.length > 0 && (
                    <div>
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-2">
                        {t("Tags")}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {customTags.map((tag) => {
                          const active = editingCustomTags.includes(tag.id);
                          return (
                            <button
                              key={tag.id}
                              onClick={() =>
                                setEditingCustomTags(
                                  active
                                    ? editingCustomTags.filter(
                                        (id) => id !== tag.id,
                                      )
                                    : [...editingCustomTags, tag.id],
                                )
                              }
                              className={cn(
                                "text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border transition-all",
                                active
                                  ? "ring-2 ring-offset-1 opacity-100"
                                  : "opacity-60 hover:opacity-100",
                              )}
                              style={{
                                background: `${tag.color}20`,
                                borderColor: `${tag.color}40`,
                                color: tag.color,
                              }}
                            >
                              {active && (
                                <Check size={9} className="inline mr-1" />
                              )}
                              {tag.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      isLoading={modalActionLoading === "classification"}
                      disabled={modalActionLoading !== null}
                      icon={<Check className="h-3.5 w-3.5" />}
                      onClick={async () => {
                        setModalActionLoading("classification");
                        try {
                          await handleSaveClassification(selectedResponse.id);
                        } finally {
                          setModalActionLoading(null);
                        }
                      }}
                      className="rounded-lg h-8! min-h-[32px]! py-1! border! border-purple-300! text-purple-700! bg-purple-50! hover:bg-purple-100! hover:text-purple-700! shadow-xs! hover:shadow-xs!"
                    >
                      <span className="text-xs">
                        {t("Salvar Classificação")}
                      </span>
                    </Button>
                  </div>
                </div>
              </div>
            </Modal>
          )}

          {/* Modal post de origem */}
          {showOriginModal && (
            <Modal
              open={showOriginModal}
              onClose={() => {
                setShowOriginModal(false);
                setOriginPost(null);
              }}
              title={t("Post de Origem")}
            >
              <div className="p-4 min-w-[300px]">
                {originPostLoading ? (
                  <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
                    {t("Buscando post...")}
                  </div>
                ) : originPost ? (
                  <div className="space-y-4">
                    {originPost.media_url && (
                      <img
                        src={originPost.media_url}
                        alt="Post"
                        className="w-full rounded-xl object-cover max-h-64"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    )}
                    {originPost.caption && (
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap line-clamp-6">
                        {originPost.caption}
                      </p>
                    )}
                    {originPost.timestamp && (
                      <p className="text-[10px] text-gray-400">
                        {t("Publicado em")}{" "}
                        {new Date(originPost.timestamp).toLocaleString("pt-BR")}
                      </p>
                    )}
                    {originPost.permalink && (
                      <a
                        href={originPost.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center text-sm font-bold text-white bg-blue-500 hover:bg-blue-600 rounded-xl py-2.5 transition-colors"
                      >
                        {t("Abrir no")}{" "}
                        {originPost.platform === "instagram"
                          ? "Instagram"
                          : "Facebook"}{" "}
                        ↗
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    {t("Não foi possível carregar o post de origem.")}
                  </div>
                )}
              </div>
            </Modal>
          )}

          {/* Modal de criação/edição de template */}
          {showTemplateModal && (
            <Modal
              open={showTemplateModal}
              onClose={() => setShowTemplateModal(false)}
              closeOnOverlayClick
              title={
                editingTemplate ? t("Editar Template") : t("Novo Template")
              }
              icon={<Bot className="h-5 w-5" />}
              maxWidth="max-w-lg"
              footer={
                <div className="flex flex-wrap items-center justify-end gap-2 w-full">
                  <Button
                    size="sm"
                    disabled={templateSaving}
                    onClick={() => setShowTemplateModal(false)}
                    className="rounded-lg h-9! w-auto! border! border-gray-300! text-gray-600! bg-white! hover:bg-gray-50! hover:text-gray-700! shadow-xs! hover:shadow-xs!"
                  >
                    <span className="text-xs">{t("Cancelar")}</span>
                  </Button>
                  <Button
                    size="sm"
                    isLoading={templateSaving}
                    disabled={templateSaving}
                    icon={<Check className="h-3.5 w-3.5" />}
                    onClick={handleSaveTemplate}
                    className="rounded-lg h-9! w-auto! border! border-purple-300! text-purple-700! bg-purple-50! hover:bg-purple-100! hover:text-purple-700! shadow-xs! hover:shadow-xs!"
                  >
                    <span className="text-xs">{t("Salvar Template")}</span>
                  </Button>
                </div>
              }
            >
              <div className="space-y-4">
                <Input
                  label={t("Nome")}
                  value={templateForm.name}
                  onChange={(e) =>
                    setTemplateForm({ ...templateForm, name: e.target.value })
                  }
                  placeholder={t("Ex.: Saudação Padrão")}
                />
                <Textarea
                  label={t("Conteúdo")}
                  value={templateForm.content}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setTemplateForm({
                      ...templateForm,
                      content: e.target.value,
                    })
                  }
                  placeholder={t(
                    "Texto que a IA usará como base para a resposta...",
                  )}
                  rows={4}
                />
                <Input
                  label={t("Palavras-chave de gatilho")}
                  hint={t("Separadas por vírgula")}
                  value={templateForm.trigger_keywords}
                  onChange={(e) =>
                    setTemplateForm({
                      ...templateForm,
                      trigger_keywords: e.target.value,
                    })
                  }
                  placeholder={t("olá, oi, bom dia")}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label={t("Plataforma")}
                    size="sm"
                    value={templateForm.platform}
                    onChange={(v) =>
                      setTemplateForm({ ...templateForm, platform: v })
                    }
                    options={[
                      { value: "all", label: t("Todas as Redes") },
                      {
                        value: "instagram",
                        label: "Instagram",
                        icon: (
                          <span
                            className={getPlatformConfig("instagram").color}
                          >
                            <PlatformIcon platform="instagram" />
                          </span>
                        ),
                      },
                      {
                        value: "facebook",
                        label: "Facebook",
                        icon: (
                          <span className={getPlatformConfig("facebook").color}>
                            <PlatformIcon platform="facebook" />
                          </span>
                        ),
                      },
                      {
                        value: "linkedin",
                        label: "LinkedIn",
                        icon: (
                          <span className={getPlatformConfig("linkedin").color}>
                            <PlatformIcon platform="linkedin" />
                          </span>
                        ),
                      },
                      {
                        value: "messenger",
                        label: "Messenger",
                        icon: (
                          <span
                            className={getPlatformConfig("messenger").color}
                          >
                            <PlatformIcon platform="messenger" />
                          </span>
                        ),
                      },
                      {
                        value: "google_my_business",
                        label: "Google My Business",
                        icon: (
                          <span
                            className={
                              getPlatformConfig("google_my_business").color
                            }
                          >
                            <PlatformIcon platform="google_my_business" />
                          </span>
                        ),
                      },
                    ]}
                  />
                  <Select
                    label={t("Status")}
                    size="sm"
                    value={templateForm.is_active ? "active" : "paused"}
                    onChange={(v) =>
                      setTemplateForm({
                        ...templateForm,
                        is_active: v === "active",
                      })
                    }
                    options={[
                      {
                        value: "active",
                        label: t("Ativo"),
                        icon: <CheckCircle />,
                      },
                      {
                        value: "paused",
                        label: t("Pausado"),
                        icon: <Clock />,
                      },
                    ]}
                  />
                </div>
              </div>
            </Modal>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default AutoResponder;
